import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import axios from "axios";
import axiosInstance from '../../utils/axiosInstance';
import config from "../../config.json";
import url from "../../url.json";
import io from "socket.io-client";
import { sendAnalyticsEvent } from '../../utils/analytics';
import Graph from "react-vis-network-graph";
import D3IdeaWall from "../../components/D3IdeaWall";
import { ViewNode } from "../../components/ViewNode";
import { genEdge, genNode } from "../../utils/ideaTool";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import StudentWallNavbar from '../../components/StudentWallNavbar';
import WallDrawer from '../../components/WallComponents/WallDrawer';
import '../../styles/StudentWall.css';

export default function IdeaWall() {
    const { courseDispatchId, groupId: urlGroupId } = useParams();
    const navigate = useNavigate();
    
    // 添加狀態來檢查用戶角色和權限
    const [userRole, setUserRole] = useState('student');
    const [isTeacher, setIsTeacher] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // 載入狀態追蹤（用於區分"尚未載入"和"已載入但為空"）
    const [tasksLoaded, setTasksLoaded] = useState(false); // 任務列表是否已載入完成
    const [nodesLoaded, setNodesLoaded] = useState(false); // 節點是否已載入完成
    const [isLoadingTasks, setIsLoadingTasks] = useState(false); // 任務列表載入中
    const [isLoadingNodes, setIsLoadingNodes] = useState(false); // 節點載入中
    const [isSwitchingGroup, setIsSwitchingGroup] = useState(false); // 切換小組中
    const [isSubmittingTask, setIsSubmittingTask] = useState(false); // 提交任務中

    const [graph, setGraph] = useState({
        nodes: [],
        edges: [],
    });
    const [graphKey, setGraphKey] = useState(0);
    const [open, setOpen] = useState(false);
    const [nodeContent, setNodeContent] = useState(null);
    const [ws, setSocket] = useState(null);
    const networkRef = useRef(null);
    const [timeFilter, setTimeFilter] = useState(0);
    const [groupFilter, setGroupFilter] = useState('all');
    const [allNodes, setAllNodes] = useState([]);
    const [isFilterPopupVisible, setFilterPopupVisible] = useState(false);
    const [keywordFilter, setKeywordFilter] = useState('');
    const [currentWall, setCurrentWall] = useState('idea');
    const [tasks, setTasks] = useState([]);
    const [groupId, setGroupId] = useState(sessionStorage.getItem('groupId'));
    
    // 任務提交對話框狀態
    const [openSubmissionDialog, setOpenSubmissionDialog] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [submissionText, setSubmissionText] = useState('');
    
    // 任務公告區域（從真實API獲取）
    const [taskAnnouncements, setTaskAnnouncements] = useState([]);
    const [isTaskDrawerExpanded, setIsTaskDrawerExpanded] = useState(true);
    
    // D3.js 視覺化模式切換
    const [visualizationMode, setVisualizationMode] = useState('vis-network'); // 'vis-network' 或 'd3'
    const [focusNodeId, setFocusNodeId] = useState(null); // 聚焦節點 ID
    
    // 小組切換相關狀態
    const [allGroups, setAllGroups] = useState([]); // 所有小組列表
    const [myGroupId, setMyGroupId] = useState(null); // 用戶所在的小組ID
    const [viewingGroupId, setViewingGroupId] = useState(null); // 當前查看的小組ID
    const [isGroupSelectDialogOpen, setIsGroupSelectDialogOpen] = useState(false); // 小組選擇對話框
    const [allowCrossGroupView, setAllowCrossGroupView] = useState(true); // 是否允許跨組查看（預設為允許）
    
    // 使用 useRef 存儲 handleGroupChange 函數，避免在 useEffect 依賴中造成初始化順序問題
    const handleGroupChangeRef = useRef(null);

    const wallConfig = {
        idea: true,
        question: true,
        cross_group: true
    };

    // 節點類型定義
    const nodeTypes = [
        { key: 'idea', label: '想法', icon: '💡' },
        { key: 'question', label: '問題', icon: '❓' },
        { key: 'information', label: '資訊', icon: '🔍' },
        { key: 'experiment', label: '實驗', icon: '🧪' },
        { key: 'record', label: '記錄', icon: '📄' },
        { key: 'reply', label: '回覆', icon: '💬' }
    ];

    // Tool Use / Reading 事件（學生進入/離開想法牆）
    useEffect(() => {
        const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
        if (!courseDispatchId || !actorId) return;
        
        // 發送開始事件
        sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseStarted', tool: 'idea-wall', actorId });
        sendAnalyticsEvent({ courseDispatchId, type: 'ReadingStarted', tool: 'idea-wall', actorId, objectId: 'idea-wall', objectType: 'Wall' });
        
        // 發送結束事件的函數
        const sendEndEvents = (useBeacon = false) => {
            sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseEnded', tool: 'idea-wall', actorId, useBeacon });
            sendAnalyticsEvent({ courseDispatchId, type: 'ReadingEnded', tool: 'idea-wall', actorId, objectId: 'idea-wall', objectType: 'Wall', useBeacon });
        };
        
        // 處理頁面關閉或切換（使用 sendBeacon 確保事件發送）
        const handleBeforeUnload = () => {
            sendEndEvents(true); // 使用 sendBeacon
        };
        
        // 處理頁面隱藏（切換到其他標籤或最小化）
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // 頁面隱藏時發送結束事件
                sendEndEvents(false);
            } else {
                // 頁面重新顯示時發送開始事件
                sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseStarted', tool: 'idea-wall', actorId });
                sendAnalyticsEvent({ courseDispatchId, type: 'ReadingStarted', tool: 'idea-wall', actorId, objectId: 'idea-wall', objectType: 'Wall' });
            }
        };
        
        // 處理頁面卸載（pagehide 比 beforeunload 更可靠）
        const handlePageHide = () => {
            sendEndEvents(true); // 使用 sendBeacon
        };
        
        // 註冊事件監聽器
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // cleanup：正常離開頁面時發送結束事件
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            sendEndEvents(false);
            
            // 如果正在查看其他組別，切換回原小組
            const currentViewingGroupId = sessionStorage.getItem('viewingGroupId');
            const myGroupIdValue = sessionStorage.getItem('myGroupId');
            if (myGroupIdValue && currentViewingGroupId && String(currentViewingGroupId) !== String(myGroupIdValue)) {
                console.log('🔄 [cleanup] 檢測到正在查看其他組別，切換回原小組');
                console.log('  - 當前查看的小組ID:', currentViewingGroupId);
                console.log('  - 原小組ID:', myGroupIdValue);
                sessionStorage.setItem('viewingGroupId', myGroupIdValue);
                sessionStorage.setItem('groupId', myGroupIdValue);
            }
        };
    }, [courseDispatchId]);

    // 獲取課程派發資訊（包含跨組查看權限）
    const fetchCourseDispatch = async () => {
        try {
            // 使用新的 info 端點，專門為學生端設計
            const response = await axiosInstance.get(`${config.getCourseDispatchDetail}${courseDispatchId}/info`);
            console.log('🔍 [Student] fetchCourseDispatch 響應:', response.data);
            if (response.data.success && response.data.data) {
                const courseDispatch = response.data.data;
                const allowView = courseDispatch.allow_cross_group_view !== false; // 預設為 true，只有明確設為 false 才禁止
                console.log('🔍 [Student] 設置 allowCrossGroupView:', allowView, '原始值:', courseDispatch.allow_cross_group_view);
                setAllowCrossGroupView(allowView);
            }
        } catch (error) {
            console.error('❌ [Student] 獲取課程派發資訊失敗:', error);
            console.error('  - 錯誤詳情:', error.response?.data);
            // 如果獲取失敗，預設允許跨組查看
            setAllowCrossGroupView(true);
        }
    };

    // 獲取小組列表
    const fetchGroups = async () => {
        try {
            const response = await axiosInstance.get(`${config.getCourseDispatchGroups}${courseDispatchId}/groups`);
            if (response.data.success) {
                const groups = response.data.data || [];
                setAllGroups(groups);
                
                // 找到當前用戶所在的小組
                const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
                if (userId) {
                    const myGroup = groups.find(g => 
                        Array.isArray(g.GroupMembers) && 
                        g.GroupMembers.some(m => 
                            String(m.user_id) === String(userId) || 
                            String(m.User?.id || '') === String(userId)
                        )
                    );
                    if (myGroup) {
                        const myGroupIdStr = String(myGroup.id);
                        setMyGroupId(myGroupIdStr);
                        sessionStorage.setItem('myGroupId', myGroupIdStr); // 保存到 sessionStorage，供 fetchTasks 使用
                        
                        // 頁面重整時，優先回到用戶自己的小組（除非 URL 明確指定其他小組）
                        // 如果 URL 沒有指定 groupId，則使用用戶自己的小組
                        const activeGroupId = urlGroupId || myGroupIdStr;
                        console.log('🔄 [fetchGroups] 設定查看的小組:', {
                            urlGroupId,
                            myGroupIdStr,
                            activeGroupId,
                            '來源': urlGroupId ? 'URL參數' : '用戶自己的小組'
                        });
                        setViewingGroupId(activeGroupId);
                        setGroupId(activeGroupId);
                        sessionStorage.setItem('groupId', activeGroupId);
                        sessionStorage.setItem('viewingGroupId', activeGroupId); // 保存到 sessionStorage，供 WebSocket 使用
                        
                        // 返回 activeGroupId，供後續函數使用
                        return activeGroupId;
                    }
                }
            }
        } catch (error) {
            console.error('獲取小組列表失敗:', error);
        }
        // 如果沒有找到用戶的小組，返回 null 或預設值
        return urlGroupId || sessionStorage.getItem('groupId') || null;
    };

  // 获取任务列表 - 統一從任務節點API獲取任務公告
    const fetchTasks = async () => {
    try {
      // 設置載入狀態
      setIsLoadingTasks(true);
      
      // 優先從 sessionStorage 讀取最新的 viewingGroupId，避免閉包問題
      // 然後是 viewingGroupId 狀態，最後是 URL 參數或 sessionStorage 的 groupId
      const latestViewingGroupId = sessionStorage.getItem('viewingGroupId') || viewingGroupId;
      const currentViewingGroupId = latestViewingGroupId || urlGroupId || sessionStorage.getItem('groupId');
      const myGroupIdValue = myGroupId || sessionStorage.getItem('myGroupId');
      
      console.log('🔍 [fetchTasks] 小組ID檢查:', {
        'latestViewingGroupId (sessionStorage)': sessionStorage.getItem('viewingGroupId'),
        'viewingGroupId (state)': viewingGroupId,
        'urlGroupId': urlGroupId,
        'sessionStorage groupId': sessionStorage.getItem('groupId'),
        'currentViewingGroupId (最終使用)': currentViewingGroupId,
        'myGroupIdValue': myGroupIdValue
      });
      
      // 如果正在查看其他小組，不獲取任務公告（因為公告區只在查看自己小組時顯示）
      if (myGroupIdValue && String(currentViewingGroupId) !== String(myGroupIdValue)) {
        console.log('⚠️ [fetchTasks] 正在查看其他小組，不獲取任務公告');
        setTaskAnnouncements([]);
        setTasksLoaded(true); // 標記為已載入（即使是空的）
        setIsLoadingTasks(false);
        return;
      }
      
      // 使用統一的任務節點API
      try {
        // 使用最新的 viewingGroupId（從 sessionStorage 或狀態）
        const activeGroupId = currentViewingGroupId || '1';
        
        console.log('✅ [fetchTasks] 使用的小組ID:', activeGroupId, '來源:', 
          sessionStorage.getItem('viewingGroupId') ? 'sessionStorage viewingGroupId' : 
          (viewingGroupId ? 'viewingGroupId state' : 
          (urlGroupId ? 'URL參數' : 'sessionStorage groupId')));
        
        // 統一從任務節點API獲取所有任務相關的節點
        const response = await axiosInstance.get(`${config.getTaskNodes}${courseDispatchId}?groupId=${activeGroupId}`);
        
        if (response.data.success) {
          const taskNodes = response.data.data || [];
          
          console.log('📥 [fetchTasks] 從統一任務節點API獲取到的任務節點數量:', taskNodes.length);
          console.log('📋 [fetchTasks] 所有原始任務節點:');
          taskNodes.forEach((node, index) => {
            console.log(`  - 原始節點 ${index + 1}:`, {
              id: node.id,
              task_id: node.task_id,
              group_id: node.group_id,
              title: node.title,
              node_type: node.node_type,
              created_at: node.created_at
            });
          });
          
          // 先針對同一任務（task_id）+ 同一小組（group_id）做去重：保留每個任務-小組組合中的任務公告節點
          // 優先保留老師的任務公告節點（非 reply 類型），而不是學生的回覆節點
          const dedupByTaskIdAndGroup = () => {
            const byTaskAndGroup = new Map();
            for (const node of taskNodes) {
              // 使用 task_id + group_id 作為唯一鍵，確保不會覆蓋不同小組的任務
              const key = node.task_id ? `${node.task_id}_${node.group_id}` : `node-${node.id}`;
              const prev = byTaskAndGroup.get(key);
              
              console.log(`🔍 [去重] 檢查節點 ${node.id}:`, {
                id: node.id,
                title: node.title,
                task_id: node.task_id,
                group_id: node.group_id,
                node_type: node.node_type,
                is_teacher_key_question: node.is_teacher_key_question,
                created_at: node.created_at,
                key: key,
                hasPrev: !!prev
              });
              
              if (!prev) {
                byTaskAndGroup.set(key, node);
                console.log(`  ✅ 新增到 Map（首次）`);
              } else {
                console.log(`  🔄 發現重複鍵，使用優先級規則選擇:`);
                console.log(`    - 舊節點 (Map中):`, {
                  id: prev.id,
                  title: prev.title,
                  task_id: prev.task_id,
                  node_type: prev.node_type,
                  is_teacher_key_question: prev.is_teacher_key_question,
                  created_at: prev.created_at
                });
                console.log(`    - 新節點:`, {
                  id: node.id,
                  title: node.title,
                  task_id: node.task_id,
                  node_type: node.node_type,
                  is_teacher_key_question: node.is_teacher_key_question,
                  created_at: node.created_at
                });
                
                // 優先級規則：
                // 1. 老師的任務公告節點（is_teacher_key_question === true）優先級最高
                // 2. 非 reply 類型的節點優先於 reply 類型
                // 3. 時間較新的節點優先
                
                const prevIsTeacherTask = prev.is_teacher_key_question === true;
                const curIsTeacherTask = node.is_teacher_key_question === true;
                const prevIsReply = prev.node_type === 'reply';
                const curIsReply = node.node_type === 'reply';
                
                let shouldReplace = false;
                let reason = '';
                
                if (curIsTeacherTask && !prevIsTeacherTask) {
                  shouldReplace = true;
                  reason = '新節點是老師任務公告，優先級高';
                } else if (!curIsTeacherTask && prevIsTeacherTask) {
                  shouldReplace = false;
                  reason = '舊節點是老師任務公告，保持優先級';
                } else if (!curIsReply && prevIsReply) {
                  shouldReplace = true;
                  reason = '新節點非 reply，優先級高於 reply';
                } else if (curIsReply && !prevIsReply) {
                  shouldReplace = false;
                  reason = '舊節點非 reply，保持優先級';
                } else {
                  // 同類型節點，按時間排序
                  const prevTime = new Date(prev.created_at || prev.createdAt || 0).getTime();
                  const curTime = new Date(node.created_at || node.createdAt || 0).getTime();
                  shouldReplace = curTime > prevTime;
                  reason = shouldReplace ? '新節點時間更新' : '舊節點時間更新';
                }
                
                if (shouldReplace) {
                  byTaskAndGroup.set(key, node);
                  console.log(`  ✅ 替換為新節點 (${reason})`);
                } else {
                  console.log(`  ⏭️ 保留舊節點 (${reason})`);
                }
              }
            }
            console.log(`📊 [去重] 去重後保留 ${byTaskAndGroup.size} 個任務節點`);
            console.log(`📋 [去重] Map 中的所有節點:`, Array.from(byTaskAndGroup.entries()).map(([key, node]) => ({
              key: key,
              id: node.id,
              task_id: node.task_id,
              group_id: node.group_id,
              title: node.title,
              node_type: node.node_type,
              is_teacher_key_question: node.is_teacher_key_question
            })));
            return Array.from(byTaskAndGroup.values());
          };

          const taskNodesDeduped = dedupByTaskIdAndGroup();
          
          console.log(`📊 [fetchTasks] 去重後的任務節點:`, taskNodesDeduped.length);
          console.log(`📋 [fetchTasks] 所有任務節點列表:`, taskNodesDeduped.map(n => ({
            id: n.id,
            task_id: n.task_id,
            group_id: n.group_id,
            title: n.title,
            node_type: n.node_type
          })));
          
          // 僅保留當前視角小組的任務節點，避免跨組資料覆蓋
          // 同時過濾掉跨組討論和問題牆的學生節點
          const taskNodesForActiveGroup = taskNodesDeduped.filter(n => {
            // 1. 過濾掉跨組討論的任務節點（不應該出現在想法牆的任務公告）
            if (n.wall_type === 'cross_group') {
              console.log('⚠️ [fetchTasks] 過濾掉跨組討論的任務節點:', {
                id: n.id,
                title: n.title,
                task_id: n.task_id,
                wall_type: n.wall_type
              });
              return false;
            }
            
            // 2. 檢查 group_id，確保只顯示當前小組的任務
            // 嘗試多種可能的 group_id 欄位名稱
            const nodeGroupId = n.group_id || n.groupId || n.group?.id || n.Group?.id || null;
            
            // 如果節點完全沒有 group_id 相關欄位，可能是數據問題，過濾掉
            if (nodeGroupId === null || nodeGroupId === undefined) {
              console.log('⚠️ [fetchTasks] 任務節點沒有 group_id 相關欄位，過濾掉（數據異常）:', {
                id: n.id,
                title: n.title,
                task_id: n.task_id,
                wall_type: n.wall_type
              });
              return false;
            }
            
            const nodeGroupIdStr = String(nodeGroupId);
            const currentGroupIdStr = String(activeGroupId || '');
            const matches = nodeGroupIdStr === currentGroupIdStr;
            
            if (!matches) {
              console.log('⚠️ [fetchTasks] 過濾掉其他小組的任務節點:', {
                id: n.id,
                title: n.title,
                task_id: n.task_id,
                wall_type: n.wall_type,
                node_group_id: nodeGroupId,
                current_group_id: activeGroupId
              });
            } else {
              console.log('✅ [fetchTasks] 保留當前小組的任務節點:', {
                id: n.id,
                title: n.title,
                task_id: n.task_id,
                wall_type: n.wall_type,
                node_group_id: nodeGroupId
              });
            }
            
            return matches;
          });
          
          console.log(`🔍 [fetchTasks] 過濾後屬於當前小組（${activeGroupId}）的任務節點數量:`, taskNodesForActiveGroup.length);
          console.log(`📋 [fetchTasks] 過濾後的任務節點:`, taskNodesForActiveGroup.map(n => ({
            id: n.id,
            task_id: n.task_id,
            group_id: n.group_id,
            title: n.title
          })));

          // 根據 node_type 獲取對應的圖標和類型標籤
          const getNodeTypeInfo = (nodeType) => {
            const typeMap = {
              'idea': { icon: '💡', label: '想法任務' },
              'question': { icon: '❓', label: '提問任務' },
              'info': { icon: '🔍', label: '資訊任務' },
              'experiment': { icon: '🧪', label: '實驗任務' },
              'record': { icon: '📄', label: '紀錄任務' },
              'task': { icon: '📋', label: '任務公告' }
            };
            return typeMap[nodeType] || { icon: '📋', label: '任務公告' };
          };
          
          // 轉換為任务公告格式，只顯示主要任務節點
          // 使用 Promise.allSettled 確保即使某些任務狀態獲取失敗，其他任務也能正常顯示
          const announcementPromises = taskNodesForActiveGroup.filter(node => {
            // 過濾掉想法收集節點（title 包含 "想法收集" 的節點）
            if (node.title && node.title.includes('想法收集')) {
              return false;
            }
            // 過濾掉學生的回覆節點（node_type === 'reply'）
            if (node.node_type === 'reply') {
              console.log('⚠️ [任務公告] 過濾掉學生回覆節點:', {
                id: node.id,
                title: node.title,
                node_type: node.node_type,
                wall_type: node.wall_type
              });
              return false;
            }
            // 過濾掉問題牆的學生問題（wall_type === 'question' 且 node_type === 'question' 且不是老師的關鍵提問）
            if (node.wall_type === 'question' && node.node_type === 'question') {
              // 只保留老師的關鍵提問（使用 is_teacher_key_question 標記）
              if (!node.is_teacher_key_question) {
                console.log('⚠️ [任務公告] 過濾掉問題牆的學生問題:', {
                  id: node.id,
                  title: node.title,
                  node_type: node.node_type,
                  wall_type: node.wall_type,
                  is_teacher_key_question: node.is_teacher_key_question
                });
                return false;
              }
            }
            // 只保留任務節點（node_type === 'task' 或其他任務相關類型）
            // 或者有 task_id 且不是回覆的節點，且是老師創建的
            return true;
            }).map(async (node) => {
            const nodeTypeInfo = getNodeTypeInfo(node.node_type);
            
            // 清理標題：移除開頭的 emoji 和可能的 Unicode 字符問題
            let cleanTitle = node.title || '任務公告';
            
            // 移除開頭的 emoji 和空格
            cleanTitle = cleanTitle.replace(/^[💡📄🧪🔍❓📋]\s*/, '');
            
            // 處理可能的編碼問題，移除特殊字符
            cleanTitle = cleanTitle.replace(/[\ud800-\udfff]/g, '');
            
            // 檢查是否有對應的想法收集節點（表示這是 use_brainstorm_wall = true 的任務）
            // 現在brainstorm_wall的節點是在問題牆上生成的，所以我們需要檢查問題牆是否有對應的節點
            const hasBrainstormNode = taskNodes.some(taskNode => 
              taskNode.task_id === node.task_id && 
              taskNode.wall_type === 'question' &&
              taskNode.title && 
              taskNode.title.includes('關鍵提問')
            );
            
            // 問題牆啟用狀態
            // ApplicationTask 目前使用 use_brainstorm_wall 來表示「此任務使用問題牆／腦力激盪牆」
            // 在課程派發建立時，已從備課包的 enable_question_wall 映射到 use_brainstorm_wall
            const hasQuestionWall =
              node.Task?.use_brainstorm_wall === true ||
              node.Task?.use_brainstorm_wall === 'true' ||
              node.Task?.use_brainstorm_wall === 1;
              
              // 獲取任務狀態信息
              let taskStatus = 'pending';
              let taskSubmission = null;
              
              if (node.task_id) {
                try {
                  console.log('🔍 [fetchTasks] 獲取任務狀態，task_id:', node.task_id);
                  const statusResponse = await axiosInstance.get(`${config.getGroupTaskStatus}${node.task_id}/group-status?groupId=${activeGroupId}`);
                  console.log('📥 [fetchTasks] 狀態響應:', statusResponse.data);
                  
                  if (statusResponse.data.success) {
                    const statusData = statusResponse.data.data;
                    
                    // 找到當前用戶的提交記錄
                    const currentUserId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
                    console.log('👤 [fetchTasks] ========== 解析任務狀態 ==========');
                    console.log('  - 任務ID:', node.task_id);
                    console.log('  - 當前用戶ID:', currentUserId);
                    console.log('  - 成員提交記錄數量:', statusData.member_submissions?.length || 0);
                    console.log('  - 完整成員提交記錄:', JSON.stringify(statusData.member_submissions, null, 2));
                    
                    const currentUserSubmission = statusData.member_submissions?.find(sub => 
                      sub.member && sub.member.id == currentUserId && sub.submission
                    );
                    
                    console.log('🔍 [fetchTasks] 當前用戶提交記錄查找:');
                    console.log('  - 是否找到當前用戶提交:', !!currentUserSubmission);
                    if (currentUserSubmission) {
                        console.log('  - 當前用戶提交內容:', currentUserSubmission.submission.submission_content?.substring(0, 50));
                        console.log('  - 當前用戶提交狀態:', currentUserSubmission.submission.status);
                    }
                    
                    // 找到最新的提交記錄（任何組員的）
                    const latestSubmission = statusData.member_submissions
                      ?.filter(sub => sub.submission)
                      ?.sort((a, b) => new Date(b.submission.submitted_at || b.submission.created_at) - new Date(a.submission.submitted_at || a.submission.created_at))[0];
                    
                    console.log('🔍 [fetchTasks] 最新提交記錄查找:');
                    console.log('  - 是否找到最新提交:', !!latestSubmission);
                    if (latestSubmission) {
                        console.log('  - 最新提交者ID:', latestSubmission.member?.id);
                        console.log('  - 最新提交者名稱:', latestSubmission.member?.name);
                        console.log('  - 最新提交內容:', latestSubmission.submission.submission_content?.substring(0, 50));
                        console.log('  - 最新提交狀態:', latestSubmission.submission.status);
                        console.log('  - 最新提交時間:', latestSubmission.submission.submitted_at || latestSubmission.submission.created_at);
                    }
                    
                    // 檢查是否為系統自動生成的狀態變更訊息（在函數外部定義，這裡需要確保可以訪問）
                    const isSystemMsg = (content) => {
                        if (!content || typeof content !== 'string') return false;
                        return content.includes('組員在想法牆上發表節點') || 
                               content.includes('任務自動開始') ||
                               content.includes('原狀態：');
                    };
                    
                    if (currentUserSubmission && currentUserSubmission.submission) {
                      // 過濾掉系統自動生成的訊息
                      const content = currentUserSubmission.submission.submission_content;
                      if (!isSystemMsg(content) || currentUserSubmission.submission.status === 'completed') {
                        taskSubmission = {
                          submission_content: content,
                          status: currentUserSubmission.submission.status,
                          submitted_at: currentUserSubmission.submission.submitted_at,
                          is_latest: currentUserSubmission.submission.is_latest
                        };
                        console.log('✅ [fetchTasks] 使用當前用戶提交記錄');
                        console.log('  - 提交記錄:', taskSubmission);
                        // 優先使用提交記錄的狀態
                        taskStatus = taskSubmission.status;
                      } else {
                        console.log('⚠️ [fetchTasks] 當前用戶提交記錄是系統自動生成的訊息，跳過');
                      }
                    } else if (latestSubmission && latestSubmission.submission) {
                      // 如果當前用戶沒有提交，但組內有其他人提交，顯示最新提交
                      // 過濾掉系統自動生成的訊息
                      const content = latestSubmission.submission.submission_content;
                      if (!isSystemMsg(content) || latestSubmission.submission.status === 'completed') {
                        taskSubmission = {
                          submission_content: content,
                          status: latestSubmission.submission.status,
                          submitted_at: latestSubmission.submission.submitted_at,
                          is_latest: latestSubmission.submission.is_latest,
                          submitted_by: latestSubmission.member?.name || '組員'
                        };
                        console.log('✅ [fetchTasks] 使用組內最新提交記錄');
                        console.log('  - 提交記錄:', taskSubmission);
                        // 使用最新提交的狀態
                        taskStatus = taskSubmission.status;
                      } else {
                        console.log('⚠️ [fetchTasks] 組內最新提交記錄是系統自動生成的訊息，跳過');
                      }
                    } else {
                      console.log('⚠️ [fetchTasks] 未找到任何提交記錄');
                      // 如果沒有提交記錄，使用 group_status 的狀態
                      taskStatus = statusData.group_status?.status || statusData.group_status || 'pending';
                    }
                    
                    console.log('📊 [fetchTasks] 最終解析結果:');
                    console.log('  - 任務狀態:', taskStatus);
                    console.log('  - 提交記錄:', taskSubmission);
                    console.log('========== 解析任務狀態結束 ==========');
                  } else {
                    console.log('❌ [fetchTasks] 獲取任務狀態失敗:', statusResponse.data.message);
                  }
                } catch (error) {
                  console.error('❌ [fetchTasks] 獲取任務狀態錯誤:', error);
                }
              }
            
            return {
              id: node.id, // 保留節點ID用於顯示
              taskId: node.task_id, // 添加任務ID用於API調用
              groupId: node.group_id || activeGroupId, // 標記所屬小組，供更新時比對
              type: node.node_type,
              typeLabel: nodeTypeInfo.label,
              icon: nodeTypeInfo.icon,
              title: cleanTitle,
              content: node.content || '',
              author: node.author_name || '老師',
              time: new Date(node.created_at).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
              createdAt: node.created_at, // 保存原始時間戳供 ViewNode 使用
              status: node.status || 'active',
              statusText: '進行中',
              statusColor: node.color || '#e3f2fd',
              nodeType: node.node_type,
              wallType: node.wall_type,
              requiresSubmission: !node.Task?.is_optional, // 從後端資料獲取
              hasBrainstormWall: hasBrainstormNode, // 添加標記表示是否有想法收集功能
              hasQuestionWall, // 此任務是否啟用了問題牆
              taskStatus: taskStatus, // 從API獲取真實狀態
              taskSubmission: taskSubmission // 任務提交信息
            };
          });
          
          // 使用 Promise.allSettled 確保即使某些任務狀態獲取失敗，其他任務也能正常顯示
          const announcementResults = await Promise.allSettled(announcementPromises);
          
          // 過濾出成功的結果，並記錄失敗的任務
          const announcements = [];
          announcementResults.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              announcements.push(result.value);
            } else {
              console.error(`❌ [fetchTasks] 任務 ${taskNodesForActiveGroup[index]?.task_id || taskNodesForActiveGroup[index]?.id} 狀態獲取失敗:`, result.reason);
              // 即使狀態獲取失敗，也添加基本任務信息（沒有提交記錄）
              const node = taskNodesForActiveGroup[index];
              if (node) {
                const nodeTypeInfo = getNodeTypeInfo(node.node_type);
                let cleanTitle = (node.title || '任務公告').replace(/^[💡📄🧪🔍❓📋]\s*/, '').replace(/[\ud800-\udfff]/g, '');
                announcements.push({
                  id: node.id,
                  taskId: node.task_id,
                  groupId: node.group_id || activeGroupId,
                  type: node.node_type,
                  typeLabel: nodeTypeInfo.label,
                  icon: nodeTypeInfo.icon,
                  title: cleanTitle,
                  content: node.content || '',
                  author: node.author_name || '老師',
                  time: new Date(node.created_at).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
                  createdAt: node.created_at,
                  status: node.status || 'active',
                  statusText: '進行中',
                  statusColor: node.color || '#e3f2fd',
                  nodeType: node.node_type,
                  wallType: node.wall_type,
                  requiresSubmission: !node.Task?.is_optional,
                  hasBrainstormWall: false,
                  taskStatus: 'pending', // 狀態獲取失敗時使用預設值
                  taskSubmission: null // 沒有提交記錄
                });
              }
            }
          });
          
          console.log('轉換後的任務公告:', announcements);
          console.log(`✅ [fetchTasks] 成功獲取 ${announcements.length} 個任務公告（共 ${taskNodesForActiveGroup.length} 個任務節點）`);
          
          // 標記任務列表已載入完成
          setTasksLoaded(true);
          
          // 使用函數式更新，確保不會覆蓋正在處理的 WebSocket 更新
          setTaskAnnouncements(prevTasks => {
            // 如果之前沒有任務，直接設置新任務
            if (prevTasks.length === 0) {
              return announcements;
            }
            
            // 如果有現有任務，合併更新（保留 WebSocket 可能已更新的狀態）
            const taskMap = new Map();
            
            // 先添加現有任務
            prevTasks.forEach(task => {
              const key = `${task.taskId || task.id}_${task.groupId}`;
              taskMap.set(key, task);
            });
            
            // 更新或添加新任務
            announcements.forEach(newTask => {
              const key = `${newTask.taskId || newTask.id}_${newTask.groupId}`;
              const existingTask = taskMap.get(key);
              
              if (existingTask) {
                // 如果任務已存在，保留 WebSocket 可能已更新的狀態和提交記錄
                // 但更新其他字段（如標題、內容等可能變更的字段）
                taskMap.set(key, {
                  ...existingTask,
                  ...newTask,
                  // 保留 WebSocket 更新的狀態和提交記錄（如果存在且較新）
                  taskStatus: existingTask.taskStatus || newTask.taskStatus,
                  taskSubmission: existingTask.taskSubmission || newTask.taskSubmission,
                  lastUpdated: existingTask.lastUpdated || newTask.lastUpdated
                });
              } else {
                // 新任務，直接添加
                taskMap.set(key, newTask);
              }
            });
            
            return Array.from(taskMap.values());
          });
          
          // 如果沒有從任務節點獲取到任務，嘗試從任務 API 獲取
          if (announcements.length === 0) {
            await fetchTasksFromAPI();
          }
        } else {
          await fetchTasksFromAPI();
        }
      } catch (wallNodeError) {
        console.log('從統一任務節點API獲取任務失敗，嘗試從任務 API 獲取:', wallNodeError);
        await fetchTasksFromAPI();
      }
    } catch (error) {
      console.error('獲取任務失敗:', error);
      setTasksLoaded(true); // 即使失敗也標記為已載入，避免一直顯示載入中
    } finally {
      setIsLoadingTasks(false); // 無論成功或失敗，都關閉載入狀態
    }
  };

  // 從任務 API 獲取任務列表（備用方案）
  const fetchTasksFromAPI = async () => {
        try {
            const response = await axiosInstance.get(config.getStudentTasks);
            if (response.data.success) {
                const tasksData = response.data.data || [];
                setTasks(tasksData);
        
        // 過濾出與當前課程派發相關的任務
        const relevantTasks = tasksData.filter(task => 
          task.course_dispatch_id === parseInt(courseDispatchId)
        );
                
                // 转换为任务公告格式
        const announcements = relevantTasks.map(task => ({
                    id: task.id,
                    type: task.task_type || 'task',
                    typeLabel: task.task_type || '任務',
                    icon: getTaskIcon(task.task_type),
                    title: task.title,
                    content: task.task_description || task.task_content || '',
                    author: '老師',
                    time: new Date(task.created_at).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
                    status: task.status,
                    statusText: getStatusText(task.status),
          statusColor: getStatusColor(task.status),
          requiresSubmission: !task.is_optional
                }));
                setTaskAnnouncements(announcements);
                setTasksLoaded(true); // 標記為已載入
            }
        } catch (error) {
      console.error('從任務 API 獲取任務失敗:', error);
      setTasksLoaded(true); // 即使失敗也標記為已載入
        }
    };

    const getTaskIcon = (taskType) => {
        const icons = {
            'idea': '💡',
            'question': '❓',
            'experiment': '🧪',
            'record': '📝',
            'info': '📚',
            'resource': '🔍'
        };
        return icons[taskType] || '📋';
    };

    const getStatusText = (status) => {
        const statusMap = {
            'pending': '待開始',
            'in_progress': '進行中',
            'completed': '已完成'
        };
        return statusMap[status] || '未知';
    };

    const getStatusColor = (status) => {
        const colorMap = {
            'pending': '#9E9E9E',
            'in_progress': '#FF9800',
            'completed': '#4CAF50'
        };
        return colorMap[status] || '#9E9E9E';
    };

    // 生成節點標籤的輔助函數，參考 Forum.js 的 genNode 函數
    const getNodeEmoji = (nodeType) => {
        const emojiMap = {
            'idea': '💡',
            'question': '❓',
            'experiment': '🧪',
            'record': '📄',
            'info': '🔍',
            'resource': '🔍',
            'task': '📋',
            'reply': '💬'
        };
        return emojiMap[nodeType] || '📝';
    };

    // 格式化時間戳，參考 Forum.js 的 formatTimestamp 函數
    const formatTimestamp = (timestamp) => {
        return new Intl.DateTimeFormat('zh-TW', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(new Date(timestamp));
    };

    // 從 HTML 中提取純文字
    const extractTextFromHTML = (html) => {
        if (!html) return '';
        
        // 創建臨時 DOM 元素來解析 HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // 獲取純文字內容
        let text = tempDiv.textContent || tempDiv.innerText || '';
        
        // 移除多餘的空白和換行
        text = text.replace(/\s+/g, ' ').trim();
        
        return text;
    };

    // 生成節點標籤，參考 Forum.js 的 genNode 函數格式
    const generateNodeLabel = (node) => {
        const emoji = getNodeEmoji(node.node_type);
        const author = node.author_name || node.Author?.name || '未知';
        const createdAt = node.created_at || node.createdAt;
        
        // 獲取標題，優先使用 title，否則使用 content
        let rawTitle = node.title || node.content || '無標題';
        
        // 檢查是否包含 HTML 標籤
        const hasHTMLTags = /<[^>]+>/.test(rawTitle);
        
        // 如果包含 HTML，提取純文字
        let cleanTitle = hasHTMLTags ? extractTextFromHTML(rawTitle) : rawTitle;
        
        // 移除標題開頭的圖標，避免重複顯示
        cleanTitle = cleanTitle.replace(/^[💡📄🧪🔍❓📋💬]\s*/, '');
        
        // 限制標題長度，避免節點標籤過長
        if (cleanTitle.length > 30) {
            cleanTitle = cleanTitle.substring(0, 30) + '...';
        }
        
        const timeStr = createdAt ? formatTimestamp(createdAt) : '';
        
        return `${emoji}\n\n${cleanTitle}\n\n${author}\n${timeStr}`;
    };


    const toggleFilterPopup = () => {
        setFilterPopupVisible(!isFilterPopupVisible);
    };

    // 篩選處理函數
    const handleTimeFilterChange = (event) => {
        const value = event.target.value;
        const selectedFilter = Number(value);
        setTimeFilter(selectedFilter);
    
        const filteredNodes = filterNodes(allNodes, selectedFilter, groupFilter, keywordFilter);
        console.log('時間篩選條件:', selectedFilter);
        console.log('篩選後的節點:', filteredNodes);
    
        setGraph({
            nodes: filteredNodes,
            edges: graph.edges,
        });
    };

    const handleGroupFilterChange = (event) => {
        const selectedGroup = event.target.value;
        setGroupFilter(selectedGroup);
    
        const filteredNodes = filterNodes(allNodes, timeFilter, selectedGroup, keywordFilter);
        console.log('類型篩選條件:', selectedGroup);
        console.log('篩選後的節點:', filteredNodes);
    
        setGraph({
            nodes: filteredNodes,
            edges: graph.edges,
        });
    };

    const handleKeywordFilterChange = (event) => {
        const keyword = event.target.value.trim(); // 移除多餘空格
        setKeywordFilter(keyword);
    
        const filteredNodes = filterNodes(allNodes, timeFilter, groupFilter, keyword);
        console.log('關鍵字篩選條件:', keyword);
        console.log('篩選後的節點:', filteredNodes);
    
        setGraph({
            nodes: filteredNodes,
            edges: graph.edges,
        });
    };

    // 取消全部篩選
    const handleClearAllFilters = () => {
        setTimeFilter(0);
        setGroupFilter('all');
        setKeywordFilter('');
        
        // 重置所有篩選後，顯示所有節點
        const filteredNodes = filterNodes(allNodes, 0, 'all', '');
        console.log('取消全部篩選，顯示所有節點:', filteredNodes);
        
        setGraph({
            nodes: filteredNodes,
            edges: graph.edges,
        });
    };

    const handleClickOpen = (nodeId) => {
        setNodeContent(null);
        setOpen(true);
        
        console.log('點擊節點ID:', nodeId);
        console.log('所有節點數據:', allNodes);
        
        // 發送節點級別的 Reading 事件
        const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
        if (courseDispatchId && actorId) {
            sendAnalyticsEvent({ 
                courseDispatchId, 
                type: 'ReadingStarted', 
                tool: 'idea-wall', 
                actorId,
                objectId: String(nodeId),
                objectType: 'WallNode'
            });
        }
        
        // 直接從 allNodes 狀態中獲取節點數據，確保包含完整的作者和時間信息
        const node = allNodes.find(n => String(n.id) === String(nodeId));
        console.log('找到的節點:', node);
        
        if (node) {
            // 使用原始節點數據（如果可用）或轉換後的節點數據
            const originalNode = node.originalNode || node;
            const nodeContent = {
                id: node.id,
                title: node.title || originalNode.title || '無標題',
                content: node.content || originalNode.content || '無內容',
                author: node.author || originalNode.author_name || originalNode.Author?.name || '未知',
                createdAt: node.createdAt || originalNode.created_at || new Date().toISOString()
            };
            console.log('設置的節點內容:', nodeContent);
            console.log('原始節點數據:', originalNode);
            setNodeContent(nodeContent);
        } else {
            console.log('未找到節點，嘗試調用 API');
            // 如果沒找到，回退到 API 調用
        fetchNodeData(nodeId);
        }

        if (networkRef.current) {
            networkRef.current.focus(nodeId, { scale: 1.5, offset: { x: 0, y: 0 } });
        }
    };

    const handleClose = () => {
        // 發送 ReadingEnded 事件（關閉便利貼詳情時）
        // 注意：所有關閉方式（取消按鈕、點擊外部、ESC鍵）都會觸發此函數
        if (nodeContent && nodeContent.id) {
            const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
            if (courseDispatchId && actorId) {
                sendAnalyticsEvent({ 
                    courseDispatchId, 
                    type: 'ReadingEnded', 
                    tool: 'idea-wall', 
                    actorId,
                    objectId: String(nodeContent.id),
                    objectType: 'WallNode'
                });
            }
        }
        setOpen(false);
    };

    // 處理任務公告點擊，聚焦到想法牆上的對應節點
    const handleTaskAnnouncementClick = (task) => {
        // 在想法牆上找到對應的節點並聚焦
        const correspondingNode = allNodes.find(node => 
            String(node.id) === String(task.id) || 
            (node.task_id && String(node.task_id) === String(task.id))
        );
        
        if (correspondingNode) {
            if (visualizationMode === 'vis-network' && networkRef.current) {
                // 傳統模式：使用 vis-network 的聚焦功能
                networkRef.current.focus(String(correspondingNode.id), { 
                    scale: 1.5, 
                    offset: { x: 0, y: 0 } 
                });
            } else if (visualizationMode === 'd3') {
                // D3 模式：設置聚焦節點 ID
                setFocusNodeId(String(correspondingNode.id));
                // 3秒後清除聚焦狀態
                setTimeout(() => setFocusNodeId(null), 3000);
            }
        } else {
            // 如果沒找到，打開 ViewNode 對話框作為備用方案
            const nodeContent = {
                id: task.id,
                title: task.title,
                content: task.content,
                author: task.author,
                createdAt: task.createdAt || task.time
            };
            setNodeContent(nodeContent);
            setOpen(true);
        }
    };

    const fetchNodeData = async (nodeId) => {
        try {
            // 由於沒有直接的 wall-nodes API 來獲取單個節點，我們使用已有的節點數據
            console.log('fetchNodeData: 嘗試從已有數據中獲取節點', nodeId);
            const node = allNodes.find(n => String(n.id) === String(nodeId));
            
            if (node) {
                const originalNode = node.originalNode || node;
                const nodeContent = {
                    id: node.id,
                    title: node.title || originalNode.title || '無標題',
                    content: node.content || originalNode.content || '無內容',
                    author: node.author || originalNode.author_name || originalNode.Author?.name || '未知',
                    createdAt: node.createdAt || originalNode.created_at || new Date().toISOString()
                };
                console.log('fetchNodeData: 設置節點內容', nodeContent);
                setNodeContent(nodeContent);
            } else {
                console.error('fetchNodeData: 無法找到節點', nodeId);
                // 設置一個錯誤狀態的節點內容
            setNodeContent({ 
                    id: nodeId,
                    title: '節點未找到',
                    content: '無法載入節點內容',
                    author: '系統',
                    createdAt: new Date().toISOString()
                });
            }
        } catch (err) {
            console.error('fetchNodeData 錯誤:', err);
            // 設置錯誤狀態
            setNodeContent({
                id: nodeId,
                title: '載入錯誤',
                content: '節點載入失敗',
                author: '系統',
                createdAt: new Date().toISOString()
            });
        }
    };

    const fetchGroupData = async () => {
        const joinCode = sessionStorage.getItem("joinCode");
        const userId = sessionStorage.getItem("userId");
        const activityId = sessionStorage.getItem("activityId");
        
        const enterDifferentGroupEndpoint = `${url.backendHost}${config.EnterDifferentGroup}${joinCode}/${userId}`;
        const getMyGroupEndpoint = `${url.backendHost}${config.getMyGroup}${activityId}/${userId}`;

        try {
            const response = await axios.get(enterDifferentGroupEndpoint);
            const fetchedGroupId = response.data.data[0].id;
            localStorage.setItem("groupId", fetchedGroupId);
            sessionStorage.setItem("groupId", fetchedGroupId);
            setGroupId(fetchedGroupId);
        } catch (error) {
            try {
                const response = await axios.get(getMyGroupEndpoint);
                const fetchedGroupId = response.data.data[0].id;
                localStorage.setItem("groupId", fetchedGroupId);
                sessionStorage.setItem("groupId", fetchedGroupId);
                setGroupId(fetchedGroupId);
            } catch (error) {
                console.error("獲取群組資料時出錯", error);
            }
        } finally {
            await getNodes();
        }
    };

    // 檢查用戶權限和獲取真實數據
    const checkUserPermission = async (activeGroupIdFromFetchGroups = null) => {
        try {
            setLoading(true);
            setNodesLoaded(false); // 重置節點載入狀態
            setTasksLoaded(false); // 重置任務載入狀態
            
            // 優先使用從 fetchGroups 傳入的 activeGroupId，然後是 viewingGroupId，最後是其他來源
            const activeGroupId = activeGroupIdFromFetchGroups || viewingGroupId || urlGroupId || sessionStorage.getItem('groupId') || '1';
            console.log('🔍 [checkUserPermission] 使用的小組ID:', activeGroupId, '來源:', 
                activeGroupIdFromFetchGroups ? 'fetchGroups返回值' : 
                (viewingGroupId ? 'viewingGroupId' : 
                (urlGroupId ? 'URL參數' : 'sessionStorage')));
            
            // 獲取用戶信息（從 token 或 session）
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    
                    // 先嘗試獲取特定小組的牆面節點（老師權限）
                    const [nodesResponse, edgesResponse] = await Promise.all([
                        axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea/${activeGroupId}`),
                        axiosInstance.get(`${config.getNodeEdges}${courseDispatchId}`)
                    ]);
                    
                    if (nodesResponse.data.success) {
                        // 成功獲取，說明有老師權限
                        setIsTeacher(true);
                        setUserRole('teacher');
                        // 保存到 sessionStorage，供 NodeCreator 使用
                        sessionStorage.setItem('isTeacher', 'true');
                        sessionStorage.setItem('userRole', 'teacher');
                        // 確保老師模式下，viewingGroupId 和 groupId 都正確設置
                        // 使用當前查看的小組ID（從 URL 或 activeGroupId）
                        const currentViewingGroupId = activeGroupId || urlGroupId || sessionStorage.getItem('viewingGroupId');
                        if (currentViewingGroupId) {
                            sessionStorage.setItem('viewingGroupId', currentViewingGroupId);
                            sessionStorage.setItem('groupId', currentViewingGroupId);
                            console.log('[Teacher Permission] 老師權限確認，獲取到節點，設置小組ID:', currentViewingGroupId);
                        } else {
                            console.warn('[Teacher Permission] 老師權限確認，但無法確定小組ID');
                        }
                        
                        const nodes = nodesResponse.data.data || [];
                        const edges = edgesResponse.data.success ? (edgesResponse.data.data || []) : [];
                        
                        // 處理節點和邊緣數據
                        await processWallNodesResponse(nodes, edges);
                        setNodesLoaded(true); // 標記節點已載入完成
                    } else {
                        throw new Error('無老師權限');
                    }
                } catch (error) {
                    console.log('嘗試老師權限失敗，使用學生模式:', error);
                    // 嘗試學生模式
                    setIsTeacher(false);
                    setUserRole('student');
                    // 保存到 sessionStorage，供 NodeCreator 使用
                    sessionStorage.setItem('isTeacher', 'false');
                    sessionStorage.setItem('userRole', 'student');
                    await fetchWallNodesForStudent();
                }
            } else {
                // 沒有 token，使用學生模式
                setIsTeacher(false);
                setUserRole('student');
                // 保存到 sessionStorage，供 NodeCreator 使用
                sessionStorage.setItem('isTeacher', 'false');
                sessionStorage.setItem('userRole', 'student');
                await fetchWallNodesForStudent();
            }
        } catch (error) {
            console.error('權限檢查失敗:', error);
            // 如果權限檢查失敗，嘗試使用學生模式
            setIsTeacher(false);
            setUserRole('student');
            await fetchWallNodesForStudent();
        } finally {
            setLoading(false);
        }
    };

    // 處理牆面節點響應數據
    const processWallNodesResponse = async (nodes, edges = [], isIncremental = false) => {
        if (!nodes || !Array.isArray(nodes)) {
            console.warn('沒有有效的節點數據');
            return;
        }
        
        console.log('處理節點數據，邊緣數量:', edges.length);
        
        // 先進行一次 ID 去重，避免相同 ID 的節點
        const deduplicatedNodes = [];
        const seenIds = new Set();
        
        nodes.forEach(node => {
            if (node && node.id && !seenIds.has(node.id)) {
                seenIds.add(node.id);
                deduplicatedNodes.push(node);
            }
        });
        
        console.log('原始節點數量:', nodes.length, '去重後節點數量:', deduplicatedNodes.length);
        
        // 處理邊緣數據，構建節點的 relatedTo 關係
        const edgesMap = new Map(); // from_node_id -> [to_node_id, ...]
        edges.forEach(edge => {
            if (edge && edge.from_node_id && edge.to_node_id) {
                if (!edgesMap.has(edge.from_node_id)) {
                    edgesMap.set(edge.from_node_id, []);
                }
                edgesMap.get(edge.from_node_id).push(edge.to_node_id);
            }
        });
        
        console.log('邊緣關係 Map:', Object.fromEntries(edgesMap));
        
        // 使用與 Forum.js 相同的簡單佈局方式，讓 vis-network 自動處理佈局
        const positions = {};

        // 過濾掉問題牆的學生問題和回覆節點，以及跨組討論的節點（只處理想法牆節點或老師的關鍵提問）
        const filteredNodes = deduplicatedNodes.filter(node => {
            // 過濾掉跨組討論的節點（不應該出現在想法牆）
            if (node.wall_type === 'cross_group') {
                console.log('⚠️ [想法牆] 過濾掉跨組討論節點:', {
                    id: node.id,
                    title: node.title,
                    wall_type: node.wall_type
                });
                return false;
            }
            
            // 如果是問題牆的節點（wall_type === 'question'）
            if (node.wall_type === 'question') {
                // 過濾掉學生的回覆（node_type === 'reply'）
                if (node.node_type === 'reply') {
                    console.log('⚠️ [想法牆] 過濾掉問題牆的學生回覆節點:', {
                        id: node.id,
                        title: node.title,
                        wall_type: node.wall_type,
                        node_type: node.node_type
                    });
                    return false;
                }
                // 嚴格使用後端標記 is_teacher_key_question 來判斷是否是老師的關鍵提問
                // 注意：學生的問題也會有 task_id（針對關鍵提問提出），所以不能用 task_id 判斷
                // 老師的關鍵提問的 node_type 可以是任何類型，包括 'question'
                const isTeacherKeyQuestion = node.is_teacher_key_question === true;
                
                if (isTeacherKeyQuestion) {
                    console.log('✅ [想法牆] 保留老師的關鍵提問節點:', {
                        id: node.id,
                        title: node.title,
                        wall_type: node.wall_type,
                        node_type: node.node_type,
                        task_id: node.task_id,
                        is_teacher_key_question: node.is_teacher_key_question
                    });
                    return true;
                } else {
                    // 過濾掉學生的問題（沒有 is_teacher_key_question 標記且沒有 task_id，或 node_type === 'question' 但沒有 task_id）
                    console.log('⚠️ [想法牆] 過濾掉問題牆的學生問題節點:', {
                        id: node.id,
                        title: node.title,
                        wall_type: node.wall_type,
                        node_type: node.node_type,
                        task_id: node.task_id,
                        is_teacher_key_question: node.is_teacher_key_question
                    });
                    return false;
                }
            }
            // 保留所有想法牆節點（wall_type === 'idea'）
            return true;
        });
        
        console.log('過濾後的節點數量:', filteredNodes.length, '原始節點數量:', deduplicatedNodes.length);
        
        // 過濾邊緣數據，只保留連接當前節點的邊緣
        const nodeIds = new Set(filteredNodes.map(node => node.id));
        const filteredEdges = edges.filter(edge => {
            const fromExists = nodeIds.has(edge.from_node_id);
            const toExists = nodeIds.has(edge.to_node_id);
            const isValid = fromExists && toExists;
            
            if (!isValid) {
                console.log('⚠️ [processWallNodesResponse] 過濾掉無效邊緣:', {
                    from_node_id: edge.from_node_id,
                    to_node_id: edge.to_node_id,
                    from_exists: fromExists,
                    to_exists: toExists
                });
            }
            
            return isValid;
        });
        
        // 重新構建邊緣關係 Map，只使用過濾後的邊緣
        const filteredEdgesMap = new Map();
        filteredEdges.forEach(edge => {
            if (edge && edge.from_node_id && edge.to_node_id) {
                if (!filteredEdgesMap.has(edge.from_node_id)) {
                    filteredEdgesMap.set(edge.from_node_id, []);
                }
                filteredEdgesMap.get(edge.from_node_id).push(edge.to_node_id);
            }
        });
        
        console.log('過濾後的邊緣數量:', filteredEdges.length, '原始邊緣數量:', edges.length);
        console.log('過濾後的邊緣關係 Map:', Object.fromEntries(filteredEdgesMap));
        
        // 轉換為前端格式
        const convertedNodes = filteredNodes.map(node => {
            // 使用後端返回的顏色，如果沒有則使用預設顏色
            let nodeColor;
            if (node.color) {
                // 如果後端返回的是字符串格式，轉換為對象格式
                if (typeof node.color === 'string') {
                    nodeColor = { border: node.color, background: node.color };
                } else {
                    nodeColor = node.color;
                }
            } else {
                nodeColor = getNodeColor(node.node_type);
            }

            // 清理標題，移除可能已經包含的圖標
            let cleanTitle = node.title || node.content || '無標題';
            cleanTitle = cleanTitle.replace(/^[💡📄🧪🔍❓📋💬]\s*/, '');
            
            // 確保正確提取作者名稱（優先順序：author_name > Author.name > '未知'）
            // 處理嵌套的 Author 對象，無論是普通對象還是可能被字符串化的對象
            let authorName = null;
            if (node.author_name) {
                authorName = node.author_name;
            } else if (node.Author) {
                // 處理 Author 可能是字符串化的情況
                if (typeof node.Author === 'string') {
                    try {
                        const parsedAuthor = JSON.parse(node.Author);
                        authorName = parsedAuthor.name;
                    } catch (e) {
                        // 如果不是 JSON 字符串，嘗試直接使用
                        authorName = null;
                    }
                } else if (node.Author.name) {
                    authorName = node.Author.name;
                }
            }
            const finalAuthorName = authorName || '未知';
            
            // 創建一個標準化的節點對象，確保 author_name 字段存在
            const normalizedNode = {
                ...node,
                author_name: finalAuthorName,
                // 確保 Author 對象存在且包含 name
                Author: node.Author || (authorName ? { name: authorName } : null)
            };

            return {
                id: node.id,
                // 使用新的 generateNodeLabel 函數生成完整的節點標籤，包含圖標、作者、時間
                // 使用標準化的節點對象確保 author_name 正確傳遞
                label: generateNodeLabel(normalizedNode),
                title: cleanTitle, // 修正：使用清理後的標題作為 title
                group: getNodeGroup(node.node_type),
                // 移除 color 屬性，讓 vis-network 使用 groups 設定中的顏色
                createdAt: node.created_at,
                content: node.content, // content 用於顯示詳細內容
                author: finalAuthorName,
                groupId: node.group_id,
                // 保留完整的原始節點數據，包括關聯的 Author 和 Task 信息
                originalNode: normalizedNode,
                // 使用與 Forum.js 相同的位置邏輯，讓 vis-network 自動佈局
                x: node.position_x || Math.random() * 400 + 100,
                y: node.position_y || Math.random() * 300 + 100,
                width: node.size_width || 250,
                height: node.size_height || 180,
                // 添加回覆關係
                relatedTo: filteredEdgesMap.get(node.id) || []
            };
        });
        
        // 最終檢查：確保轉換後的節點也沒有重複 ID
        const finalUniqueNodes = [];
        const finalSeenIds = new Set();
        
        convertedNodes.forEach(node => {
            if (node && node.id && !finalSeenIds.has(node.id)) {
                finalSeenIds.add(node.id);
                finalUniqueNodes.push(node);
            }
        });
        
        console.log('最終節點數量:', finalUniqueNodes.length);
        
        // 轉換邊緣為 vis-network 格式
        const convertedEdges = edges.map(edge => ({
            from: edge.from_node_id,
            to: edge.to_node_id,
            arrows: 'to',
            edge_type: edge.edge_type || 'reply' // 保存邊緣類型
        }));
        
        console.log('轉換後的邊緣數量:', convertedEdges.length);
        
        if (isIncremental) {
            // 增量更新：檢查節點是否已存在
            console.log('🔄 [增量更新] 開始處理增量更新');
            console.log('  - 待處理節點數量:', finalUniqueNodes.length);
            
            if (finalUniqueNodes.length === 0) {
                console.log('⚠️ [增量更新] 沒有新節點需要添加');
                return finalUniqueNodes;
            }
            
            setAllNodes(prev => {
                console.log('🔍 [增量更新] 檢查 allNodes 中是否已存在');
                console.log('  - 當前 allNodes 數量:', prev.length);
                console.log('  - 新節點 IDs:', finalUniqueNodes.map(n => n.id));
                
                // 過濾出不存在於 allNodes 中的新節點
                const newNodesToAdd = finalUniqueNodes.filter(newNode => {
                    const exists = prev.some(n => String(n.id) === String(newNode.id));
                if (exists) {
                    console.log('⚠️ [增量更新] 節點已存在於 allNodes，跳過:', newNode.id);
                        return false;
                }
                console.log('➕ [增量更新] 添加新節點到 allNodes:', newNode.id);
                    return true;
                });
                
                if (newNodesToAdd.length === 0) {
                    console.log('⚠️ [增量更新] 所有節點都已存在於 allNodes');
                    return prev;
                }
                
                console.log('✅ [增量更新] 添加', newNodesToAdd.length, '個新節點到 allNodes');
                console.log('  - 更新後 allNodes 數量:', prev.length + newNodesToAdd.length);
                return [...prev, ...newNodesToAdd];
            });
            
            setGraph(prevGraph => {
                console.log('🔍 [增量更新] 檢查圖表中是否已存在');
                console.log('  - 當前圖表節點數量:', prevGraph.nodes.length);
                console.log('  - 新節點 IDs:', finalUniqueNodes.map(n => n.id));
                
                // 過濾出不存在於圖表中的新節點
                const newNodesToAdd = finalUniqueNodes.filter(newNode => {
                    const exists = prevGraph.nodes.some(n => String(n.id) === String(newNode.id));
                if (exists) {
                    console.log('⚠️ [增量更新] 節點已存在於圖表中，跳過:', newNode.id);
                        return false;
                    }
                    console.log('➕ [增量更新] 添加新節點到圖表:', newNode.id);
                    return true;
                });
                
                if (newNodesToAdd.length === 0) {
                    console.log('⚠️ [增量更新] 所有節點都已存在於圖表');
                    return prevGraph;
                }
                
                console.log('✅ [增量更新] 添加', newNodesToAdd.length, '個新節點到圖表');
                const updatedNodes = [...prevGraph.nodes, ...newNodesToAdd];
                console.log('  - 更新後圖表節點數量:', updatedNodes.length);
                
                console.log('🔍 [增量更新] 應用篩選條件');
                console.log('  - 時間篩選:', timeFilter);
                console.log('  - 群組篩選:', groupFilter);
                console.log('  - 關鍵字篩選:', keywordFilter);
                
                const filteredNodes = filterNodes(updatedNodes, timeFilter, groupFilter, keywordFilter);
                console.log('  - 篩選後節點數量:', filteredNodes.length);
                
                // 過濾出不存在於圖表中的新邊緣
                const newEdgesToAdd = convertedEdges.filter(newEdge => {
                    const exists = prevGraph.edges.some(e => 
                        String(e.from) === String(newEdge.from) && 
                        String(e.to) === String(newEdge.to)
                    );
                    if (exists) {
                        console.log('⚠️ [增量更新] 邊緣已存在於圖表，跳過:', newEdge.id);
                        return false;
                    }
                    console.log('➕ [增量更新] 添加新邊緣到圖表:', newEdge.id);
                    return true;
                });
                
                console.log('  - 當前邊緣數量:', prevGraph.edges.length);
                console.log('  - 新增邊緣數量:', newEdgesToAdd.length);
                console.log('  - 更新後邊緣數量:', prevGraph.edges.length + newEdgesToAdd.length);
                
                return {
                    nodes: filteredNodes,
                    edges: [...prevGraph.edges, ...newEdgesToAdd]
                };
            });
        } else {
            // 完整更新
            setAllNodes(finalUniqueNodes);
        setGraph({
                nodes: finalUniqueNodes,
                edges: convertedEdges
            });
        }
        // 移除 setGraphKey 調用，避免重新渲染導致位置重置
        
        return finalUniqueNodes;
    };

    // 為老師獲取牆面節點
    const fetchWallNodesForTeacher = async () => {
        try {
            // 優先使用 URL 參數中的 groupId，如果老師進入特定組別視角
            const activeGroupId = urlGroupId || sessionStorage.getItem('groupId') || '1';
            
            console.log('[Teacher] 獲取牆面節點，courseDispatchId:', courseDispatchId, 'groupId:', activeGroupId);
            
            // 同時獲取節點和邊緣
            const [nodesResponse, edgesResponse] = await Promise.all([
                axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea/${activeGroupId}`),
                axiosInstance.get(`${config.getNodeEdges}${courseDispatchId}`)
            ]);
            
            console.log('[Teacher] 節點響應:', nodesResponse.data);
            console.log('[Teacher] 邊緣響應:', edgesResponse.data);
            
            if (nodesResponse.data.success) {
                const nodes = nodesResponse.data.data || [];
                const edges = edgesResponse.data.success ? (edgesResponse.data.data || []) : [];
                
                console.log('[Teacher] 獲取到節點數:', nodes.length, '邊緣數:', edges.length);
                
                await processWallNodesResponse(nodes, edges);
            }
        } catch (error) {
            console.error('[Teacher] 獲取牆面節點失敗:', error);
        }
    };

    // 為學生獲取牆面節點
    const fetchWallNodesForStudent = async () => {
        try {
            // 設置載入狀態
            setIsLoadingNodes(true);
            
            // 優先從 sessionStorage 讀取最新的 viewingGroupId，避免閉包問題
            // 然後是 viewingGroupId 狀態，最後是 URL 參數或 sessionStorage 的 groupId
            const latestViewingGroupId = sessionStorage.getItem('viewingGroupId') || viewingGroupId;
            const activeGroupId = latestViewingGroupId || urlGroupId || sessionStorage.getItem('groupId') || '1';
            
            console.log('🔍 [fetchWallNodesForStudent] 小組ID檢查:', {
                'latestViewingGroupId (sessionStorage)': sessionStorage.getItem('viewingGroupId'),
                'viewingGroupId (state)': viewingGroupId,
                'urlGroupId': urlGroupId,
                'sessionStorage groupId': sessionStorage.getItem('groupId'),
                'activeGroupId (最終使用)': activeGroupId
            });
            
            // 同時獲取想法牆、問題牆和任務節點
            const [ideaResponse, questionResponse, taskNodesResponse] = await Promise.allSettled([
                axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea/${activeGroupId}`),
                axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/question/${activeGroupId}`),
                axiosInstance.get(`${config.getTaskNodes}${courseDispatchId}?groupId=${activeGroupId}`)
            ]);
            
            // 先創建一個 Map 來避免重複節點
            const nodeMap = new Map();
            
            // 合併想法牆的節點（這些節點可能有 task_id）
            if (ideaResponse.status === 'fulfilled' && ideaResponse.value.data.success) {
                const ideaNodes = ideaResponse.value.data.data || [];
                console.log('📥 [fetchWallNodesForStudent] 想法牆節點數量:', ideaNodes.length);
                if (ideaNodes.length > 0) {
                    console.log('📥 [fetchWallNodesForStudent] 第一個想法牆節點的欄位:', {
                        '所有欄位': Object.keys(ideaNodes[0]),
                        'group_id': ideaNodes[0].group_id,
                        'groupId': ideaNodes[0].groupId,
                        'group?.id': ideaNodes[0].group?.id,
                        'Group?.id': ideaNodes[0].Group?.id,
                        '完整節點': ideaNodes[0]
                    });
                }
                ideaNodes.forEach(node => {
                    if (node && node.id) {
                        nodeMap.set(node.id, node);
                    }
                });
            }
            
            // 合併問題牆的節點（只保留老師的關鍵提問，過濾掉學生的問題和回覆）
            if (questionResponse.status === 'fulfilled' && questionResponse.value.data.success) {
                const questionNodes = questionResponse.value.data.data || [];
                questionNodes.forEach(node => {
                    if (node && node.id) {
                        // 嚴格使用後端標記 is_teacher_key_question 來判斷是否是老師的關鍵提問
                        // 注意：學生的問題也會有 task_id（針對關鍵提問提出），所以不能用 task_id 判斷
                        // 老師的關鍵提問的 node_type 可以是任何類型，包括 'question'
                        const isTeacherKeyQuestion = node.is_teacher_key_question === true;
                        
                        if (isTeacherKeyQuestion && node.node_type !== 'reply') {
                            console.log('✅ [想法牆] 保留老師的關鍵提問節點:', {
                                id: node.id,
                                title: node.title,
                                node_type: node.node_type,
                                task_id: node.task_id,
                                is_teacher_key_question: node.is_teacher_key_question
                            });
                            nodeMap.set(node.id, node);
                        } else {
                            console.log('⚠️ [想法牆] 過濾掉學生問題或回覆節點:', {
                                id: node.id,
                                title: node.title,
                                node_type: node.node_type,
                                task_id: node.task_id,
                                is_teacher_key_question: node.is_teacher_key_question
                            });
                        }
                    }
                });
            }
            
            // 處理任務節點，但要避免與已有的節點重複（依 group_id + wall_type + task_id 去重）
            if (taskNodesResponse.status === 'fulfilled' && taskNodesResponse.value.data.success) {
                const taskNodes = taskNodesResponse.value.data.data || [];
                console.log('📥 [fetchWallNodesForStudent] 任務節點數量:', taskNodes.length);
                if (taskNodes.length > 0) {
                    console.log('📥 [fetchWallNodesForStudent] 第一個任務節點的欄位:', {
                        '所有欄位': Object.keys(taskNodes[0]),
                        'group_id': taskNodes[0].group_id,
                        'groupId': taskNodes[0].groupId,
                        'group?.id': taskNodes[0].group?.id,
                        'Group?.id': taskNodes[0].Group?.id,
                        '完整節點': taskNodes[0]
                    });
                }
                
                // 對於想法牆，需要特殊處理 use_brainstorm_wall = true 的任務
                // 對於有想法收集功能的任務，在想法牆上顯示主要任務節點的內容，而不是想法收集節點
                
                // 第一步：找出所有有想法收集功能的任務ID
                const brainstormTaskIds = new Set();
                taskNodes.forEach(node => {
                    if (node.title && node.title.includes('想法收集')) {
                        brainstormTaskIds.add(node.task_id);
                    }
                });
                
                // 處理任務節點，但要檢查是否已經存在於 nodeMap 中
                taskNodes.forEach(node => {
                    if (node && node.id) {
                        // 過濾掉跨組討論的任務節點
                        if (node.wall_type === 'cross_group') {
                            console.log('⚠️ [fetchWallNodesForStudent] 過濾掉跨組討論的任務節點:', {
                                id: node.id,
                                title: node.title,
                                task_id: node.task_id,
                                wall_type: node.wall_type
                            });
                            return;
                        }
                        
                        // 處理想法牆和問題牆的任務節點
                        // 想法牆：顯示所有想法牆的任務節點
                        // 問題牆：只顯示老師的關鍵提問任務節點（is_teacher_key_question === true）
                        if (node.wall_type === 'idea') {
                            // 如果是想法收集節點且已有主要任務節點，則跳過
                            if (brainstormTaskIds.has(node.task_id) && node.title && node.title.includes('想法收集')) {
                                // 跳過想法收集節點
                                return;
                            }
                        } else if (node.wall_type === 'question') {
                            // 問題牆的任務節點：只保留老師的關鍵提問
                            if (!node.is_teacher_key_question) {
                                console.log('⚠️ [fetchWallNodesForStudent] 過濾掉問題牆的非老師關鍵提問任務節點:', {
                                    id: node.id,
                                    title: node.title,
                                    task_id: node.task_id,
                                    wall_type: node.wall_type,
                                    is_teacher_key_question: node.is_teacher_key_question
                                });
                                return;
                            }
                            console.log('✅ [fetchWallNodesForStudent] 保留問題牆的老師關鍵提問任務節點:', {
                                id: node.id,
                                title: node.title,
                                task_id: node.task_id,
                                wall_type: node.wall_type,
                                is_teacher_key_question: node.is_teacher_key_question
                            });
                        }
                        
                        // 使用複合 key 做去重：group_id + wall_type + task_id（若無 task_id 則回退到 id）
                        const compositeKey = node.task_id ? `${node.group_id || ''}|${node.wall_type || ''}|${node.task_id}` : `node-${node.id}`;
                        // 檢查 nodeMap 中是否已有同任務的節點（以 id 快取）
                        const existSameTask = Array.from(nodeMap.values()).some(existing => (
                          (node.task_id && existing.task_id && String(existing.task_id) === String(node.task_id) &&
                           String(existing.group_id) === String(node.group_id) &&
                           String(existing.wall_type) === String(node.wall_type))
                        ));

                        if (!existSameTask) {
                          nodeMap.set(node.id, node);
                        } else {
                          // 若已有同任務節點，保留較新的（比較 created_at）
                          for (const [eid, existing] of nodeMap.entries()) {
                            const isSame = node.task_id && existing.task_id && String(existing.task_id) === String(node.task_id) &&
                                           String(existing.group_id) === String(node.group_id) &&
                                           String(existing.wall_type) === String(node.wall_type);
                            if (isSame) {
                              const exTime = new Date(existing.created_at || existing.createdAt || 0).getTime();
                              const curTime = new Date(node.created_at || node.createdAt || 0).getTime();
                              if (curTime > exTime) {
                                nodeMap.delete(eid);
                                nodeMap.set(node.id, node);
                              }
                              break;
                            }
                          }
                        }
                    }
                });
            }
            
            // 從 nodeMap 轉換為數組，這樣就已經去重了
            let uniqueNodes = Array.from(nodeMap.values());
            
            // 添加詳細 log 檢查節點的所有欄位
            console.log('🔍 [fetchWallNodesForStudent] ========== 節點欄位檢查 ==========');
            console.log('當前 activeGroupId:', activeGroupId);
            console.log('節點總數（過濾前）:', uniqueNodes.length);
            
            if (uniqueNodes.length > 0) {
                console.log('第一個節點的所有欄位:', Object.keys(uniqueNodes[0]));
                console.log('第一個節點的完整資料:', uniqueNodes[0]);
                console.log('第一個節點的 group_id 相關欄位:', {
                    'group_id': uniqueNodes[0].group_id,
                    'groupId': uniqueNodes[0].groupId,
                    'group.id': uniqueNodes[0].group?.id,
                    'Group.id': uniqueNodes[0].Group?.id,
                    '所有包含 group 的欄位': Object.keys(uniqueNodes[0]).filter(k => k.toLowerCase().includes('group'))
                });
            }
            
            // 再次過濾，確保只顯示當前小組的節點，並過濾掉不應該出現在想法牆的節點
            uniqueNodes = uniqueNodes.filter(node => {
                // 1. 過濾掉跨組討論的節點（不應該出現在想法牆）
                if (node.wall_type === 'cross_group') {
                    console.log('⚠️ [fetchWallNodesForStudent] 過濾掉跨組討論節點:', {
                        id: node.id,
                        title: node.title?.substring(0, 30),
                        wall_type: node.wall_type
                    });
                    return false;
                }
                
                // 2. 過濾掉問題牆的學生問題和回覆（只保留老師的關鍵提問）
                // 注意：任務節點（有 task_id）如果是問題牆類型，且是老師派發的（is_teacher_key_question === true），應該顯示
                if (node.wall_type === 'question') {
                    // 過濾掉學生的回覆
                    if (node.node_type === 'reply') {
                        console.log('⚠️ [fetchWallNodesForStudent] 過濾掉問題牆的學生回覆:', {
                            id: node.id,
                            title: node.title?.substring(0, 30),
                            wall_type: node.wall_type,
                            node_type: node.node_type
                        });
                        return false;
                    }
                    // 如果是任務節點（有 task_id），且是老師的關鍵提問，保留它
                    if (node.task_id && node.is_teacher_key_question) {
                        console.log('✅ [fetchWallNodesForStudent] 保留問題牆的老師關鍵提問任務節點:', {
                            id: node.id,
                            title: node.title?.substring(0, 30),
                            wall_type: node.wall_type,
                            node_type: node.node_type,
                            task_id: node.task_id,
                            is_teacher_key_question: node.is_teacher_key_question
                        });
                        // 繼續後續的 group_id 檢查
                    } else if (node.node_type === 'question' && !node.is_teacher_key_question) {
                        // 過濾掉學生的問題（只保留老師的關鍵提問）
                        console.log('⚠️ [fetchWallNodesForStudent] 過濾掉問題牆的學生問題:', {
                            id: node.id,
                            title: node.title?.substring(0, 30),
                            wall_type: node.wall_type,
                            node_type: node.node_type,
                            is_teacher_key_question: node.is_teacher_key_question
                        });
                        return false;
                    }
                }
                
                // 3. 檢查 group_id，確保只顯示當前小組的節點
                // 嘗試多種可能的 group_id 欄位名稱
                const nodeGroupId = node.group_id || node.groupId || node.group?.id || node.Group?.id || null;
                
                // 如果節點完全沒有 group_id 相關欄位，可能是數據問題，過濾掉
                if (nodeGroupId === null || nodeGroupId === undefined) {
                    console.log('⚠️ [fetchWallNodesForStudent] 節點沒有 group_id 相關欄位，過濾掉（數據異常）:', {
                        id: node.id,
                        title: node.title?.substring(0, 30),
                        wall_type: node.wall_type
                    });
                    return false;
                }
                
                // 詳細 log
                console.log('🔍 [過濾檢查] 節點:', {
                    id: node.id,
                    title: node.title?.substring(0, 30),
                    wall_type: node.wall_type,
                    'node.group_id': node.group_id,
                    'node.groupId': node.groupId,
                    'node.group?.id': node.group?.id,
                    'node.Group?.id': node.Group?.id,
                    '最終 nodeGroupId': nodeGroupId,
                    'currentGroupId': activeGroupId
                });
                
                const nodeGroupIdStr = String(nodeGroupId);
                const currentGroupIdStr = String(activeGroupId || '');
                const matches = nodeGroupIdStr === currentGroupIdStr;
                
                if (!matches) {
                    console.log('⚠️ [fetchWallNodesForStudent] 過濾掉其他小組的節點:', {
                        id: node.id,
                        title: node.title?.substring(0, 30),
                        wall_type: node.wall_type,
                        node_group_id: nodeGroupId,
                        node_group_id_str: nodeGroupIdStr,
                        current_group_id: activeGroupId,
                        current_group_id_str: currentGroupIdStr,
                        '是否匹配': matches
                    });
                } else {
                    console.log('✅ [fetchWallNodesForStudent] 保留當前小組的節點:', {
                        id: node.id,
                        title: node.title?.substring(0, 30),
                        wall_type: node.wall_type,
                        node_group_id: nodeGroupId
                    });
                }
                
                return matches;
            });
            
            console.log('========== 節點欄位檢查結束 ==========');
            
            console.log('獲取到的所有牆面節點數量（過濾後）:', uniqueNodes.length);
            console.log('節點詳情:', uniqueNodes.map(n => ({
                id: n.id,
                title: n.title,
                node_type: n.node_type,
                wall_type: n.wall_type,
                group_id: n.group_id
            })));
            
            // 獲取邊緣數據
            const edgesResponse = await axiosInstance.get(`${config.getNodeEdges}${courseDispatchId}`);
            let edgesData = [];
            if (edgesResponse.data.success) {
                edgesData = edgesResponse.data.data || [];
                console.log('獲取到的邊緣數量:', edgesData.length);
            }
            
            await processWallNodesResponse(uniqueNodes, edgesData);
            
            // 標記節點已載入完成
            setNodesLoaded(true);
            
        } catch (error) {
            console.error('獲取學生牆面節點失敗:', error);
            // 設置空數據
            setAllNodes([]);
            setGraph({
                nodes: [],
                edges: []
            });
            setNodesLoaded(true); // 即使失敗也標記為已載入
        } finally {
            setIsLoadingNodes(false); // 無論成功或失敗，都關閉載入狀態
        }
    };

    // 根據節點類型獲取顏色
    const getNodeColor = (nodeType) => {
        const colorMap = {
            'reply': { border: '#9E9E9E', background: '#FFFFFF' },      // 回覆 - 白色
            'idea': { border: '#FFC107', background: '#FFF9C4' },       // 想法 - 黃色
            'question': { border: '#2196F3', background: '#BBDEFB' },   // 提問 - 藍色
            'record': { border: '#00BCD4', background: '#B2EBF2' },     // 記錄 - 青色
            'experiment': { border: '#F44336', background: '#FFCDD2' }, // 實驗 - 紅色
            'info': { border: '#4CAF50', background: '#C8E6C9' } // 資訊 - 綠色
        };
        return colorMap[nodeType] || { border: '#CCCCCC', background: '#EEEEEE' };
    };

    // 根據節點類型獲取分組
    const getNodeGroup = (nodeType) => {
        const groupMap = {
            'reply': 'reply',
            'idea': 'idea',
            'question': 'question',
            'record': 'record',
            'experiment': 'experiment',
            'information': 'information', // 與 Forum.js 一致
            'info': 'information' // 映射到 information
        };
        return groupMap[nodeType] || 'idea';
    };

    // 篩選節點函數
    const filterNodes = (nodes, timeFilter, groupFilter, keywordFilter = '') => {
        console.log('🔍 filterNodes 開始篩選 - 總節點數:', nodes.length);
        console.log('🔍 篩選條件 - 時間:', timeFilter, '群組:', groupFilter, '關鍵字:', keywordFilter);
        
        const now = new Date();
    
        // 如果沒有篩選條件，返回所有節點
        if (timeFilter === 0 && groupFilter === 'all' && !keywordFilter) {
            console.log('✅ 無篩選條件，返回所有節點:', nodes.length);
            return nodes;
        }
    
        const filteredNodes = nodes.filter((node) => {
            // 時間篩選
            let passesTimeFilter = true;
            if (node.createdAt && !isNaN(Date.parse(node.createdAt))) {
                const nodeDate = new Date(node.createdAt);
                if (timeFilter > 0) {
                    passesTimeFilter = now - nodeDate <= timeFilter * 60 * 1000;
                }
            }
    
            // 類型篩選
            const matchesGroupFilter = groupFilter === 'all' || node.group === groupFilter;
    
            // 關鍵字篩選（檢查 label 和 title 是否包含關鍵字）
            const matchesKeywordFilter =
                !keywordFilter || // 如果關鍵字為空，視為通過篩選
                (node.title && node.title.toLowerCase().includes(keywordFilter.toLowerCase())) || // title 包含關鍵字
                (node.label && node.label.toLowerCase().includes(keywordFilter.toLowerCase()));   // label 包含關鍵字
    
            // 只有通過所有篩選條件的節點才會被保留
            const passesAllFilters = passesTimeFilter && matchesGroupFilter && matchesKeywordFilter;
            
            if (!passesAllFilters) {
                console.log('❌ 節點被篩選掉:', node.id, node.title, {
                    passesTimeFilter,
                    matchesGroupFilter,
                    matchesKeywordFilter,
                    timeFilter,
                    groupFilter,
                    keywordFilter
                });
            }
            
            return passesAllFilters;
        });
    
        console.log('✅ 篩選完成 - 原始節點數:', nodes.length, '篩選後節點數:', filteredNodes.length);
        return filteredNodes;
    };

    useEffect(() => {
        async function fetchData() {
            // 將 courseDispatchId 存入 sessionStorage，供 NodeCreator 使用
            if (courseDispatchId) {
                sessionStorage.setItem('courseDispatchId', courseDispatchId);
            }
            
            // 先獲取課程派發資訊（包含跨組查看權限）
            await fetchCourseDispatch();
            
            // 獲取小組列表（會自動設定 myGroupId 和 viewingGroupId）
            // 頁面重整時，fetchGroups 會自動設定 viewingGroupId 為用戶自己的小組（除非 URL 有指定）
            const activeGroupId = await fetchGroups();
            
            // 將 activeGroupId 傳給 checkUserPermission，確保使用一致的小組ID
            await checkUserPermission(activeGroupId);
            await fetchTasks();
            setSocket(io.connect(url.socketioHost, {path: '/s/socket.io'}));
        }
        fetchData();
    }, [courseDispatchId, urlGroupId]);

    // 當 viewingGroupId 改變時，重新獲取節點和任務
    useEffect(() => {
        if (viewingGroupId && courseDispatchId && !isTeacher) {
            console.log('🔄 [useEffect] viewingGroupId 改變，重新獲取節點和任務:', viewingGroupId);
            
            // 使用標記來追蹤當前的獲取操作，避免競態條件
            let isCancelled = false;
            
            // 設置切換小組載入狀態
            setIsSwitchingGroup(true);
            setTasksLoaded(false); // 重置任務載入狀態
            setNodesLoaded(false); // 重置節點載入狀態
            
            // 先清空所有狀態，避免顯示舊組的資料
            setAllNodes([]);
            setGraph({
                nodes: [],
                edges: []
            });
            setTasks([]); // 清空舊的任務列表
            setTaskAnnouncements([]); // 清空任務公告區
            setGraphKey(prev => prev + 1); // 強制重新渲染圖表
            
            // 使用 setTimeout 確保狀態清空後再獲取數據，避免競態條件
            // 保存當前的 viewingGroupId，避免閉包問題
            const currentViewingGroupId = viewingGroupId;
            const timer = setTimeout(async () => {
                if (isCancelled) return;
                
                // 確保使用最新的 viewingGroupId（從 sessionStorage 讀取，避免閉包問題）
                const latestViewingGroupId = sessionStorage.getItem('viewingGroupId') || currentViewingGroupId;
                console.log('🔄 [useEffect] 準備獲取數據，使用的小組ID:', latestViewingGroupId);
                
                // 重新獲取該小組的節點和任務
                // 先獲取任務，再獲取節點，確保任務公告先顯示
                // 傳入最新的 viewingGroupId 確保使用正確的小組ID
                await fetchTasks();
                if (isCancelled) return;
                await fetchWallNodesForStudent();
                
                // 關閉切換小組載入狀態
                if (!isCancelled) {
                    setIsSwitchingGroup(false);
                }
            }, 100); // 增加延遲時間，確保狀態清空完成
            
            return () => {
                clearTimeout(timer);
                isCancelled = true;
                setIsSwitchingGroup(false); // 清理時也關閉載入狀態
            };
        }
    }, [viewingGroupId]);

    // WebSocket 即時更新監聽
    useEffect(() => {
        if (ws && courseDispatchId) {
            console.log('初始化 WebSocket 監聽，courseDispatchId:', courseDispatchId, 'viewingGroupId:', viewingGroupId);
            
            ws.on('connect', () => {
                console.log('WebSocket 已連接');
            });

            // 監聽新節點事件
            ws.on(`node-recieve-${courseDispatchId}`, async (body) => {
                console.log('🔔 [WebSocket] ========== 收到新節點事件 ==========');
                console.log('  - 完整數據:', JSON.stringify(body, null, 2));
                console.log('  - 節點 ID:', body.id);
                console.log('  - 節點標題:', body.title);
                console.log('  - 節點類型:', body.node_type);
                console.log('  - 節點內容:', body.content);
                console.log('  - 作者ID:', body.author_id);
                console.log('  - 作者名稱:', body.author_name);
                console.log('  - 小組ID:', body.group_id);
                console.log('  - 課程派發ID:', body.course_dispatch_id);
                console.log('  - 任務ID:', body.task_id);
                console.log('  - 父節點ID:', body.parent_node_id);
                console.log('  - 牆面類型:', body.wall_type);
                console.log('  - 狀態:', body.status);
                console.log('  - 創建時間:', body.created_at);
                
                // 從 sessionStorage 獲取最新的 viewingGroupId，確保使用最新值
                const latestViewingGroupId = sessionStorage.getItem('viewingGroupId') || viewingGroupId;
                const activeGroupId = latestViewingGroupId || urlGroupId || sessionStorage.getItem('groupId');
                console.log('📍 [WebSocket] 小組檢查');
                console.log('  - 當前查看的小組ID (viewingGroupId):', viewingGroupId);
                console.log('  - 最新查看的小組ID (latestViewingGroupId):', latestViewingGroupId);
                console.log('  - 當前小組ID (activeGroupId):', activeGroupId);
                console.log('  - 新節點小組ID:', body.group_id);
                
                // 使用字符串比較確保類型一致
                const nodeGroupIdStr = String(body.group_id || '');
                const currentGroupIdStr = String(activeGroupId || '');
                const groupMatches = nodeGroupIdStr === currentGroupIdStr;
                
                console.log('  - 節點小組ID (字串):', nodeGroupIdStr);
                console.log('  - 當前小組ID (字串):', currentGroupIdStr);
                console.log('  - 是否匹配:', groupMatches);
                
                // 只處理與當前小組相關的節點，且只處理想法牆的節點
                // 過濾掉問題牆的學生回覆（node_type === 'reply'）
                // 只保留老師的關鍵提問（嚴格使用 is_teacher_key_question 標記）
                // 注意：學生的問題也會有 task_id，所以不能用 task_id 判斷
                // 老師的關鍵提問的 node_type 可以是任何類型，包括 'question'
                // 過濾掉跨組討論的節點
                if (body.wall_type === 'cross_group') {
                    console.log('⚠️ [WebSocket] 過濾掉跨組討論節點，不在想法牆顯示');
                    return;
                }
                
                const isQuestionWallReply = body.wall_type === 'question' && body.node_type === 'reply';
                const isIdeaWallNode = body.wall_type === 'idea';
                const isTeacherKeyQuestion = body.is_teacher_key_question === true;
                
                if (groupMatches && (isIdeaWallNode || isTeacherKeyQuestion)) {
                    if (isQuestionWallReply) {
                        console.log('⚠️ [WebSocket] 過濾掉問題牆的學生回覆節點，不在想法牆顯示');
                        return;
                    }
                    
                    console.log('✅ [WebSocket] 新節點屬於當前查看的小組且為想法牆節點或老師關鍵提問，準備添加到畫面');
                    
                    // 使用增量更新模式處理新節點
                    const convertedNodes = await processWallNodesResponse([body], [], true);
                    console.log('🔄 [WebSocket] 轉換後的新節點:', convertedNodes);
                    console.log('  - 轉換節點數量:', convertedNodes?.length);
                    
                    // 檢查是否為想法牆節點且與任務相關，如果是則自動觸發任務狀態更新
                    // 注意：只有在查看自己小組時才更新任務狀態（因為公告區只在查看自己小組時顯示）
                    if (body.wall_type === 'idea' && myGroupId && String(viewingGroupId) === String(myGroupId)) {
                        console.log('🔍 [WebSocket] 檢測到想法牆節點且正在查看自己小組，檢查是否與任務相關');
                        console.log('  - 節點ID:', body.id);
                        console.log('  - 節點類型:', body.node_type);
                        console.log('  - 父節點ID:', body.parent_node_id);
                        console.log('  - 任務ID:', body.task_id);
                        await checkAndUpdateTaskStatusFromReply(body);
                    } else {
                        console.log('ℹ️ [WebSocket] 跳過任務狀態檢查（非想法牆節點或正在查看其他小組）');
                    }
                    
                    console.log('✅ [WebSocket] 新節點已通過增量更新處理完成');
                } else {
                    if (body.wall_type === 'cross_group') {
                        console.log('⚠️ [WebSocket] 忽略跨組討論節點（不在想法牆顯示）');
                    } else if (isQuestionWallReply || (!isIdeaWallNode && !isTeacherKeyQuestion)) {
                        console.log('⚠️ [WebSocket] 忽略問題牆的學生問題或回覆節點（不在想法牆顯示）');
                    } else if (!groupMatches) {
                        console.log('⚠️ [WebSocket] 新節點不屬於當前查看的小組，跳過處理:', {
                            '節點小組ID': nodeGroupIdStr,
                            '當前查看小組ID': currentGroupIdStr
                        });
                    } else {
                        console.log('⚠️ [WebSocket] 新節點不符合顯示條件，跳過處理');
                    }
                }
                console.log('========== 新節點事件處理結束 ==========');
            });

            // 監聽課程派發設定更新事件（跨組查看權限變更）
            ws.on(`course-dispatch-settings-updated-${courseDispatchId}`, (data) => {
                console.log('🔔 [WebSocket] ========== 收到課程設定更新事件 ==========');
                console.log('  - 完整數據:', JSON.stringify(data, null, 2));
                console.log('  - 課程派發ID:', data.course_dispatch_id);
                console.log('  - 允許跨組查看:', data.allow_cross_group_view);
                console.log('  - 更新時間:', data.updated_at);
                
                const newAllowCrossGroupView = data.allow_cross_group_view !== false;
                
                // 使用函數式更新，確保獲取最新的狀態值
                setAllowCrossGroupView(prevAllowCrossGroupView => {
                    console.log('  - 之前狀態:', prevAllowCrossGroupView);
                    console.log('  - 新狀態:', newAllowCrossGroupView);
                    
                    // 如果老師關閉了跨組查看，且學生正在查看其他小組，則切換回自己的小組
                    if (prevAllowCrossGroupView && !newAllowCrossGroupView) {
                        // 使用 setTimeout 確保狀態更新後再檢查
                        setTimeout(() => {
                            const currentViewingGroupId = viewingGroupId || sessionStorage.getItem('viewingGroupId');
                            const currentMyGroupId = myGroupId || sessionStorage.getItem('myGroupId');
                            
                            if (currentViewingGroupId && currentMyGroupId && String(currentViewingGroupId) !== String(currentMyGroupId)) {
                                console.log('⚠️ [WebSocket] 老師已關閉跨組查看，切換回自己的小組');
                                console.log('  - 當前查看小組:', currentViewingGroupId);
                                console.log('  - 自己的小組:', currentMyGroupId);
                                
                                // 切換回自己的小組（使用 ref 訪問最新的函數）
                                if (handleGroupChangeRef.current) {
                                    handleGroupChangeRef.current(currentMyGroupId);
                                }
                            }
                        }, 100);
                    }
                    
                    return newAllowCrossGroupView;
                });
                
                console.log('✅ [WebSocket] 課程設定更新處理完成');
            });

            // 監聽任務狀態更新事件（新增）
            ws.on(`task-status-updated-${courseDispatchId}`, async (data) => {
                console.log('🔔 [WebSocket] ========== 收到任務狀態更新事件 ==========');
                console.log('  - 完整數據:', JSON.stringify(data, null, 2));
                console.log('  - 任務 ID:', data.task_id);
                console.log('  - 小組 ID:', data.group_id);
                console.log('  - 新狀態:', data.status);
                console.log('  - 更新者 ID:', data.updated_by);
                console.log('  - 更新時間:', data.updated_at);
                console.log('  - 提交內容:', data.submission_content?.substring(0, 50));
                console.log('  - 組員信息:', data.group_members);
                
                // 從 sessionStorage 獲取最新的 viewingGroupId，確保使用最新值
                const latestViewingGroupId = sessionStorage.getItem('viewingGroupId') || viewingGroupId;
                const activeGroupId = latestViewingGroupId || urlGroupId || sessionStorage.getItem('groupId');
                console.log('📍 [WebSocket] 小組檢查:');
                console.log('  - 當前查看的小組ID (viewingGroupId):', viewingGroupId);
                console.log('  - 最新查看的小組ID (latestViewingGroupId):', latestViewingGroupId);
                console.log('  - 當前小組ID (activeGroupId):', activeGroupId);
                console.log('  - 事件小組ID:', data.group_id);
                console.log('  - 是否匹配:', String(data.group_id) === String(activeGroupId));
                
                // 只處理與當前小組相關的任務狀態更新（使用嚴格字符串比較）
                if (String(data.group_id) === String(activeGroupId)) {
                    console.log('✅ [WebSocket] 任務狀態更新屬於當前小組，準備更新界面');
                    console.log('  - 當前任務公告數量:', taskAnnouncements.length);
                    console.log('  - 當前任務公告:', taskAnnouncements.map(t => ({
                        id: t.id,
                        taskId: t.taskId,
                        title: t.title,
                        taskStatus: t.taskStatus
                    })));
                    
                    // 重新獲取最新的任務狀態，確保顯示最新的提交內容（包括覆蓋的情況）
                    try {
                        console.log('🔄 [WebSocket] 重新獲取任務狀態以確保數據最新');
                        const statusResponse = await axiosInstance.get(`${config.getGroupTaskStatus}${data.task_id}/group-status?groupId=${activeGroupId}`);
                        
                        if (statusResponse.data.success) {
                            const statusData = statusResponse.data.data;
                            const currentUserId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
                            
                            // 找到當前用戶的提交記錄
                            const currentUserSubmission = statusData.member_submissions?.find(sub => 
                                sub.member && sub.member.id == currentUserId && sub.submission
                            );
                            
                            // 找到最新的提交記錄（任何組員的）
                            const latestSubmission = statusData.member_submissions
                                ?.filter(sub => sub.submission)
                                ?.sort((a, b) => new Date(b.submission.submitted_at || b.submission.created_at) - new Date(a.submission.submitted_at || a.submission.created_at))[0];
                            
                            // 檢查是否為系統自動生成的狀態變更訊息
                            const isSystemMsg = (content) => {
                                if (!content || typeof content !== 'string') return false;
                                return content.includes('組員在想法牆上發表節點') || 
                                       content.includes('任務自動開始') ||
                                       content.includes('原狀態：');
                            };
                            
                            let taskSubmission = null;
                            if (currentUserSubmission && currentUserSubmission.submission) {
                                const content = currentUserSubmission.submission.submission_content;
                                if (!isSystemMsg(content) || currentUserSubmission.submission.status === 'completed') {
                                    taskSubmission = {
                                        submission_content: content,
                                        status: currentUserSubmission.submission.status,
                                        submitted_at: currentUserSubmission.submission.submitted_at,
                                        is_latest: currentUserSubmission.submission.is_latest
                                    };
                                }
                            } else if (latestSubmission && latestSubmission.submission) {
                                const content = latestSubmission.submission.submission_content;
                                if (!isSystemMsg(content) || latestSubmission.submission.status === 'completed') {
                                    taskSubmission = {
                                        submission_content: content,
                                        status: latestSubmission.submission.status,
                                        submitted_at: latestSubmission.submission.submitted_at,
                                        is_latest: latestSubmission.submission.is_latest,
                                        submitted_by: latestSubmission.member?.name || '組員'
                                    };
                                }
                            }
                            
                            // 如果任務公告列表為空且尚未載入完成，重新獲取任務列表
                            // 但需要等待一小段時間，確保 fetchTasks 完成後再處理更新
                            // 注意：如果 tasksLoaded 為 true，表示已經載入完成，只是本來就是空的，不需要重新獲取
                            if (taskAnnouncements.length === 0 && !tasksLoaded) {
                                console.log('⚠️ [WebSocket] 任務公告列表為空且尚未載入，重新獲取任務列表');
                                await fetchTasks();
                                
                                // 等待任務列表獲取完成後，再次獲取最新狀態並更新
                                setTimeout(async () => {
                                    try {
                                        const retryStatusResponse = await axiosInstance.get(`${config.getGroupTaskStatus}${data.task_id}/group-status?groupId=${activeGroupId}`);
                                        if (retryStatusResponse.data.success) {
                                            const retryStatusData = retryStatusResponse.data.data;
                                            const currentUserId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
                                            
                                            // 找到當前用戶的提交記錄
                                            const currentUserSubmission = retryStatusData.member_submissions?.find(sub => 
                                                sub.member && sub.member.id == currentUserId && sub.submission
                                            );
                                            
                                            // 找到最新的提交記錄（任何組員的）
                                            const latestSubmission = retryStatusData.member_submissions
                                                ?.filter(sub => sub.submission)
                                                ?.sort((a, b) => new Date(b.submission.submitted_at || b.submission.created_at) - new Date(a.submission.submitted_at || a.submission.created_at))[0];
                                            
                                            const isSystemMsg = (content) => {
                                                if (!content || typeof content !== 'string') return false;
                                                return content.includes('組員在想法牆上發表節點') || 
                                                       content.includes('任務自動開始') ||
                                                       content.includes('原狀態：');
                                            };
                                            
                                            let taskSubmission = null;
                                            if (currentUserSubmission && currentUserSubmission.submission) {
                                                const content = currentUserSubmission.submission.submission_content;
                                                if (!isSystemMsg(content) || currentUserSubmission.submission.status === 'completed') {
                                                    taskSubmission = {
                                                        submission_content: content,
                                                        status: currentUserSubmission.submission.status,
                                                        submitted_at: currentUserSubmission.submission.submitted_at,
                                                        is_latest: currentUserSubmission.submission.is_latest
                                                    };
                                                }
                                            } else if (latestSubmission && latestSubmission.submission) {
                                                const content = latestSubmission.submission.submission_content;
                                                if (!isSystemMsg(content) || latestSubmission.submission.status === 'completed') {
                                                    taskSubmission = {
                                                        submission_content: content,
                                                        status: latestSubmission.submission.status,
                                                        submitted_at: latestSubmission.submission.submitted_at,
                                                        is_latest: latestSubmission.submission.is_latest,
                                                        submitted_by: latestSubmission.member?.name || '組員'
                                                    };
                                                }
                                            }
                                            
                                            // 使用函數式更新，確保基於最新狀態
                                            setTaskAnnouncements(prevTasks => {
                                                const updatedTasks = prevTasks.map(task => {
                                                    const sameTask = (task.taskId == data.task_id || task.id == data.task_id);
                                                    const sameGroup = String(task.groupId) == String(data.group_id);
                                                    if (sameTask && sameGroup) {
                                                        return {
                                                            ...task,
                                                            taskStatus: data.status,
                                                            lastUpdated: data.updated_at,
                                                            groupStatus: {
                                                                status: data.status,
                                                                updated_by: data.updated_by,
                                                                updated_at: data.updated_at
                                                            },
                                                            taskSubmission: taskSubmission || task.taskSubmission
                                                        };
                                                    }
                                                    return task;
                                                });
                                                return updatedTasks;
                                            });
                                        }
                                    } catch (retryError) {
                                        console.error('❌ [WebSocket] 重新獲取任務狀態失敗:', retryError);
                                    }
                                }, 500); // 等待 500ms 確保 fetchTasks 完成
                                
                                return; // 重新獲取後會觸發狀態更新，這裡直接返回
                            }
                            
                            // 更新任務公告狀態（使用函數式更新，確保基於最新狀態）
                            setTaskAnnouncements(prevTasks => {
                                // 如果列表為空但已載入完成，可能是本來就沒有任務，仍然可以更新（可能是新任務）
                                // 只有在未載入完成時才跳過
                                if (prevTasks.length === 0 && !tasksLoaded) {
                                    console.log('⚠️ [WebSocket] 任務列表為空且尚未載入，跳過更新');
                                    return prevTasks;
                                }
                                
                                const updatedTasks = prevTasks.map(task => {
                                    const sameTask = (task.taskId == data.task_id || task.id == data.task_id);
                                    const sameGroup = String(task.groupId) == String(data.group_id);
                                    if (sameTask && sameGroup) {
                                        console.log(`🔄 [WebSocket] 找到匹配的任務: ${task.title}`);
                                        console.log(`  - 舊狀態: ${task.taskStatus}`);
                                        console.log(`  - 新狀態: ${data.status}`);
                                        console.log(`  - 新提交內容:`, taskSubmission?.submission_content?.substring(0, 50));
                                        
                                        // 重要：如果當前狀態是 completed，不應該被覆蓋為 in_progress
                                        // 已完成後不應該再變回進行中
                                        if (task.taskStatus === 'completed' && data.status === 'in_progress') {
                                            console.log(`⚠️ [WebSocket] 任務狀態已是 completed，不應該被覆蓋為 in_progress，跳過更新`);
                                            return task; // 保持原狀態
                                        }
                                        
                                        // 優化：如果當前狀態已經是 in_progress，且新狀態也是 in_progress，跳過更新（減少資料流）
                                        // 只有在狀態變為 completed 時才需要更新
                                        if (task.taskStatus === 'in_progress' && data.status === 'in_progress') {
                                            console.log(`⏭️ [WebSocket] 任務狀態已是 in_progress，無需重複更新，跳過（減少資料流）`);
                                            return task; // 保持原狀態
                                        }
                                        
                                        const updatedTask = {
                                            ...task,
                                            taskStatus: data.status,
                                            lastUpdated: data.updated_at,
                                            groupStatus: {
                                                status: data.status,
                                                updated_by: data.updated_by,
                                                updated_at: data.updated_at
                                            },
                                            // 使用重新獲取的最新提交記錄
                                            taskSubmission: taskSubmission || task.taskSubmission
                                        };
                                        
                                        console.log('  - 更新後的任務:', updatedTask);
                                        return updatedTask;
                                    }
                                    return task;
                                });
                                
                                console.log('📊 [WebSocket] 更新前後對比:');
                                console.log('  - 更新前任務數量:', prevTasks.length);
                                console.log('  - 更新後任務數量:', updatedTasks.length);
                                console.log('  - 是否有變化:', JSON.stringify(prevTasks) !== JSON.stringify(updatedTasks));
                                
                                return updatedTasks;
                            });
                            console.log('✅ [WebSocket] 任務狀態已更新到本地狀態（已重新獲取最新數據）');
                        } else {
                            console.log('⚠️ [WebSocket] 重新獲取任務狀態失敗，使用 WebSocket 事件數據');
                            // 回退到原來的邏輯（使用函數式更新）
                            setTaskAnnouncements(prevTasks => {
                                if (prevTasks.length === 0) {
                                    console.log('⚠️ [WebSocket] 任務列表為空，跳過更新');
                                    return prevTasks;
                                }
                                
                                return prevTasks.map(task => {
                                    const sameTask = (task.taskId == data.task_id || task.id == data.task_id);
                                    const sameGroup = String(task.groupId) == String(data.group_id);
                                    if (sameTask && sameGroup) {
                                        const isCurrentUser = data.updated_by == (sessionStorage.getItem('userId') || localStorage.getItem('userId'));
                                        let submittedBy = '組員';
                                        if (Array.isArray(data.group_members)) {
                                            const matched = data.group_members.find(m => String(m.id) === String(data.updated_by));
                                            if (matched && matched.name) submittedBy = matched.name;
                                        }
                                        
                                        return {
                                            ...task,
                                            taskStatus: data.status,
                                            lastUpdated: data.updated_at,
                                            groupStatus: {
                                                status: data.status,
                                                updated_by: data.updated_by,
                                                updated_at: data.updated_at
                                            },
                                            taskSubmission: (data.submission_content ? {
                                                submission_content: data.submission_content,
                                                status: data.status,
                                                submitted_at: data.updated_at,
                                                is_latest: true,
                                                submitted_by: isCurrentUser ? (sessionStorage.getItem('userName') || localStorage.getItem('userName') || '我') : submittedBy
                                            } : task.taskSubmission)
                                        };
                                    }
                                    return task;
                                });
                            });
                        }
                    } catch (error) {
                        console.error('❌ [WebSocket] 重新獲取任務狀態失敗:', error);
                        // 回退到原來的邏輯（使用函數式更新）
                        setTaskAnnouncements(prevTasks => {
                            if (prevTasks.length === 0) {
                                console.log('⚠️ [WebSocket] 任務列表為空，跳過更新');
                                return prevTasks;
                            }
                            
                            return prevTasks.map(task => {
                                const sameTask = (task.taskId == data.task_id || task.id == data.task_id);
                                const sameGroup = String(task.groupId) == String(data.group_id);
                                if (sameTask && sameGroup) {
                                    const isCurrentUser = data.updated_by == (sessionStorage.getItem('userId') || localStorage.getItem('userId'));
                                    let submittedBy = '組員';
                                    if (Array.isArray(data.group_members)) {
                                        const matched = data.group_members.find(m => String(m.id) === String(data.updated_by));
                                        if (matched && matched.name) submittedBy = matched.name;
                                    }
                                    
                                    return {
                                        ...task,
                                        taskStatus: data.status,
                                        lastUpdated: data.updated_at,
                                        groupStatus: {
                                            status: data.status,
                                            updated_by: data.updated_by,
                                            updated_at: data.updated_at
                                        },
                                        taskSubmission: (data.submission_content ? {
                                            submission_content: data.submission_content,
                                            status: data.status,
                                            submitted_at: data.updated_at,
                                            is_latest: true,
                                            submitted_by: isCurrentUser ? (sessionStorage.getItem('userName') || localStorage.getItem('userName') || '我') : submittedBy
                                        } : task.taskSubmission)
                                    };
                                }
                                return task;
                            });
                        });
                    }
                    
                    console.log('========== 任務狀態更新事件處理結束 ==========');
                } else {
                    console.log('⚠️ [WebSocket] 任務狀態更新不屬於當前小組，跳過處理');
                    console.log('========== 任務狀態更新事件處理結束 ==========');
                }
            });

            // 監聽任務派送事件（新增）
            ws.on(`task-dispatched-${courseDispatchId}`, async (data) => {
                console.log('🔔 [WebSocket] 收到任務派送事件');
                console.log('  - 完整數據:', data);
                console.log('  - 任務 ID:', data.task_id);
                console.log('  - 小組 ID:', data.group_id);
                console.log('  - 任務標題:', data.title);
                console.log('  - 任務類型:', data.task_type);
                console.log('  - 牆面類型:', data.wall_type);
                
                // 從 sessionStorage 獲取最新的 viewingGroupId，確保使用最新值
                const latestViewingGroupId = sessionStorage.getItem('viewingGroupId') || viewingGroupId;
                const activeGroupId = latestViewingGroupId || urlGroupId || sessionStorage.getItem('groupId');
                
                // 只處理與當前小組相關的任務派送（使用嚴格字符串比較）
                if (String(data.group_id) === String(activeGroupId)) {
                    console.log('✅ [WebSocket] 任務派送屬於當前小組，準備更新界面');
                    
                    // 重新獲取任務列表
                    await fetchTasks();
                    
                    // 重新獲取牆面節點
                    if (isTeacher) {
                        await fetchWallNodesForTeacher();
                    } else {
                        await fetchWallNodesForStudent();
                    }
                    
                    console.log('✅ [WebSocket] 任務派送已處理，界面已更新');
                } else {
                    console.log('⚠️ [WebSocket] 任務派送不屬於當前小組，跳過處理');
                }
            });

            // 監聽新邊緣事件
            ws.on(`edge-recieve-${courseDispatchId}`, async (body) => {
                console.log('🔗 [WebSocket] ========== 收到新邊緣事件 ==========');
                console.log('  - 完整數據:', JSON.stringify(body, null, 2));
                console.log('  - from_node_id:', body.from_node_id);
                console.log('  - to_node_id:', body.to_node_id);
                console.log('  - edge_type:', body.edge_type);
                console.log('  - group_id:', body.group_id);
                console.log('  - course_dispatch_id:', body.course_dispatch_id);
                console.log('  - 邊緣ID:', body.id);
                
                // 直接處理所有邊緣，因為邊緣連接的是當前課程派發的節點
                const newEdge = {
                    from: body.from_node_id,
                    to: body.to_node_id,
                    id: `${body.from_node_id}-${body.to_node_id}`,
                    arrows: 'to',
                    edge_type: body.edge_type || 'reply' // 保存邊緣類型
                };
                
                console.log('🔄 [WebSocket] 準備添加邊緣到圖表');
                console.log('  - 邊緣 ID:', newEdge.id);
                console.log('  - 從節點:', newEdge.from);
                console.log('  - 到節點:', newEdge.to);
                console.log('  - 邊緣類型:', newEdge.edge_type);
                
                setGraph(prevGraph => {
                    console.log('📊 [WebSocket] 當前圖表狀態:');
                    console.log('  - 節點數量:', prevGraph.nodes.length);
                    console.log('  - 邊緣數量:', prevGraph.edges.length);
                    
                    // 檢查邊緣是否已存在
                    const exists = prevGraph.edges.some(e => e.id === newEdge.id);
                    if (exists) {
                        console.log('⚠️ [WebSocket] 邊緣已存在，跳過:', newEdge.id);
                        console.log('  - 現有邊緣:', prevGraph.edges.find(e => e.id === newEdge.id));
                        return prevGraph;
                    }
                    
                    console.log('✅ [WebSocket] 成功添加邊緣到圖表');
                    console.log('  - 當前邊緣數量:', prevGraph.edges.length);
                    console.log('  - 新增後邊緣數量:', prevGraph.edges.length + 1);
                    
                    const updatedGraph = {
                        nodes: prevGraph.nodes,
                        edges: [...prevGraph.edges, newEdge]
                    };
                    
                    console.log('📊 [WebSocket] 更新後圖表狀態:');
                    console.log('  - 節點數量:', updatedGraph.nodes.length);
                    console.log('  - 邊緣數量:', updatedGraph.edges.length);
                    
                    return updatedGraph;
                });
                console.log('========== 邊緣事件處理結束 ==========');
            });

            return () => {
                ws.off(`node-recieve-${courseDispatchId}`);
                ws.off(`edge-recieve-${courseDispatchId}`);
                ws.off(`task-status-updated-${courseDispatchId}`);
                ws.off(`task-dispatched-${courseDispatchId}`);
                ws.off(`course-dispatch-settings-updated-${courseDispatchId}`);
                ws.off('connect');
            };
        }
    }, [ws, courseDispatchId, urlGroupId, isTeacher, viewingGroupId, myGroupId]);

    // getNodes 函數已被 checkUserPermission 取代，不再需要
    // 所有數據都通過 fetchWallNodesForStudent 或 fetchWallNodesForTeacher 獲取



    // 切換牆面
    const handleWallChange = (wallType) => {
        setCurrentWall(wallType);
        
        if (wallType === 'question') {
            navigate(`/student/question-wall/${courseDispatchId}`);
        } else if (wallType === 'cross_group') {
            navigate(`/student/cross-group/${courseDispatchId}`);
        }
    };

    // 處理node類型選擇
    const handleNodeTypeSelect = (nodeType) => {
        console.log('選擇node類型:', nodeType);
        // 這裡可以實現新增node的邏輯
        // 例如打開新增node的對話框
    };

    // 處理小組切換
    const handleGroupChange = async (newGroupId) => {
        if (newGroupId === viewingGroupId) return;
        
        console.log('🔄 [handleGroupChange] 切換小組:', {
            '舊小組ID': viewingGroupId,
            '新小組ID': newGroupId,
            '是否為老師': isTeacher
        });
        
        // 記錄跨組查看的 Caliper event
        // 每次切換小組時都記錄一次查看事件
        const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
        if (courseDispatchId && actorId && newGroupId) {
            // 檢查是否為跨組查看（不是自己的小組）
            const myGroupIdValue = myGroupId || sessionStorage.getItem('myGroupId');
            const currentViewingGroupId = viewingGroupId || sessionStorage.getItem('viewingGroupId') || myGroupIdValue;
            const isCrossGroupView = myGroupIdValue && String(newGroupId) !== String(myGroupIdValue);
            
            // 如果是跨組查看或老師查看，記錄事件
            // 注意：每次切換到不同小組都算一次查看
            if (isCrossGroupView || isTeacher) {
                console.log('📊 [跨組查看] 記錄事件:', {
                    actorId,
                    from_group_id: currentViewingGroupId,
                    to_group_id: newGroupId,
                    is_teacher: isTeacher,
                    is_cross_group: isCrossGroupView
                });
                
                sendAnalyticsEvent({
                    courseDispatchId,
                    type: 'ReadingStarted',
                    tool: 'idea-wall',
                    actorId,
                    objectId: newGroupId,
                    objectType: 'Group',
                    metadata: {
                        action: 'cross_group_view',
                        from_group_id: currentViewingGroupId,
                        to_group_id: newGroupId,
                        is_teacher: isTeacher
                    }
                }).catch(err => {
                    console.warn('[跨組查看] 事件發送失敗:', err);
                });
            }
        }
        
        // 立即更新 sessionStorage，確保 NodeCreator 能獲取到最新的 groupId
        // 這對老師特別重要，因為老師需要在切換小組後能立即為新小組創建節點
        sessionStorage.setItem('viewingGroupId', String(newGroupId));
        sessionStorage.setItem('groupId', String(newGroupId));
        
        // 設置切換小組載入狀態
        setIsSwitchingGroup(true);
        setTasksLoaded(false); // 重置任務載入狀態
        setNodesLoaded(false); // 重置節點載入狀態
        
        // 先清空所有狀態，避免顯示舊組的資料
        setAllNodes([]);
        setGraph({
            nodes: [],
            edges: []
        });
        setTasks([]); // 清空舊的任務列表
        setTaskAnnouncements([]); // 清空任務公告區
        setGraphKey(prev => prev + 1); // 強制重新渲染圖表
        
        // 更新狀態
        setViewingGroupId(newGroupId);
        setGroupId(newGroupId);
        
        // 使用 setTimeout 確保狀態清空後再獲取數據，避免競態條件
        // 先獲取任務，再獲取節點，確保任務公告先顯示
        setTimeout(async () => {
            // 重新獲取該小組的節點數據和任務公告
            if (isTeacher) {
                // 老師模式：重新獲取該小組的節點（使用老師權限 API）
                try {
                    const [nodesResponse, edgesResponse] = await Promise.all([
                        axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea/${newGroupId}`),
                        axiosInstance.get(`${config.getNodeEdges}${courseDispatchId}`)
                    ]);
                    
                    if (nodesResponse.data.success) {
                        const nodes = nodesResponse.data.data || [];
                        const edges = edgesResponse.data.success ? (edgesResponse.data.data || []) : [];
                        await processWallNodesResponse(nodes, edges);
                        setNodesLoaded(true);
                        console.log('✅ [handleGroupChange] 老師模式：成功獲取新小組的節點');
                    }
                } catch (error) {
                    console.error('❌ [handleGroupChange] 老師模式：獲取節點失敗:', error);
                }
            } else {
                // 學生模式：獲取任務公告和節點
                await fetchTasks(); // 先獲取任務公告
                await fetchWallNodesForStudent(); // 再獲取節點
            }
            
            // 關閉切換小組載入狀態
            setIsSwitchingGroup(false);
        }, 100); // 增加延遲時間，確保狀態清空完成
        
        setIsGroupSelectDialogOpen(false);
    };
    
    // 使用 useEffect 更新 ref，確保 WebSocket 事件處理器能訪問最新的函數
    useEffect(() => {
        handleGroupChangeRef.current = handleGroupChange;
    }, [handleGroupChange]);

    const options = {
        layout: {
            randomSeed: 23,
            improvedLayout: true,
            hierarchical: {
                enabled: true,
                blockShifting: true,
                edgeMinimization: true,
                nodeSpacing: 150,
                direction: "RL",
                sortMethod: "hubsize",
            },
        },
        interaction: {
            navigationButtons: true,
            dragNodes: true,
            dragView: true,
            hideEdgesOnDrag: false,
            hideEdgesOnZoom: false,
            hideNodesOnDrag: false,
            hover: false,
            hoverConnectedEdges: true,
            keyboard: {
                enabled: false,
                speed: { x: 10, y: 10, zoom: 0.02 },
                bindToWindow: true,
            },
            multiselect: false,
            selectable: true,
            selectConnectedEdges: true,
            tooltipDelay: 300,
            zoomSpeed: 1,
            zoomView: true,
        },
        clickToUse: false,
        groups: {
            idea: {
                color: {
                    border: "#FFC",
                    background: "#FFC",
                    fontSize: 5,
                    highlight: {
                        border: "#FFC",
                        background: "#FFC",
                    },
                },
            },
            question: {
                color: {
                    border: "#CCF",
                    background: "#CCF",
                    highlight: {
                        border: "#CCF",
                        background: "#CCF",
                    },
                },
            },
            information: {
                color: {
                    border: "#CFC",
                    background: "#CFC",
                    highlight: {
                        border: "#CFC",
                        background: "#CFC",
                    },
                },
            },
            experiment: {
                color: {
                    border: "#FFDBDB",
                    background: "#FFDBDB",
                    highlight: {
                        border: "#FFDBDB",
                        background: "#FFDBDB",
                    },
                },
            },
            record: {
                color: {
                    border: "#B9DCF4",
                    background: "#B9DCF4",
                    highlight: {
                        border: "#B9DCF4",
                        background: "#B9DCF4",
                    },
                },
            },
            reply: {
                color: {
                    border: "#FFF",
                    background: "#FFF",
                    highlight: {
                        border: "#FFF",
                        background: "#FFF",
                    },
                },
            },
        },
        physics: {
            enabled: false, // 關閉物理引擎，避免彈開效果
            stabilization: {
                enabled: false,
                iterations: 0,
                updateInterval: 0,
                onlyDynamicEdges: false,
                fit: false
            },
            barnesHut: {
                gravitationalConstant: -2000,
                centralGravity: 0.3,
                springLength: 95,
                springConstant: 0.04,
                damping: 0.09,
                avoidOverlap: 0.5
            },
            maxVelocity: 50,
            minVelocity: 0.1,
            solver: 'barnesHut',
            timestep: 0.5,
            adaptiveTimestep: true
        },
        edges: {
            color: "#8B8B8B",
            width: 1,
            length: 600,
            arrows: {
                from: {
                    enabled: true,
                    scaleFactor: 0.7,
                },
                to: {
                    enabled: false,
                },
            },
        },
        nodes: {
            shape: "box",
            borderWidth: 1,
            shapeProperties: {
                borderRadius: 1,
            },
            opacity: 1,
            fixed: false, // 修正：允許節點拖曳
            font: {
                color: "#343434",
                size: 14, // 修正：字體大小從 2 改為 14
                face: "arial",
                background: "none",
                strokeWidth: 0,
                strokeColor: "#ffffff",
                align: "left",
                multi: false,
                vadjust: 0,
                bold: {
                    color: "#343434",
                    size: 14, // 修正：粗體字體大小也改為 14
                    face: "arial",
                    vadjust: 0,
                    mod: "bold",
                },
            },
            hidden: false,
            label: "HTML",
            level: undefined,
            margin: 10,
            shadow: {
                color: "rgba(33,33,33,.7)",
                size: 10,
                x: 10,
                y: 10,
            },
            heightConstraint: { minimum: 100, valign: "middle" },
            widthConstraint: { minimum: 100, maximum: 100 },
            mass: 1,
            scaling: {
                label: {
                    enabled: true,
                    min: 16,
                    max: 16,
                    drawThreshold: 12,
                },
                customScalingFunction: function (min, max, total, value) {
                    if (max === min) {
                        return 0.5;
                    } else {
                        let scale = 1 / (max - min);
                        return Math.max(0, (value - min) * scale);
                    }
                },
            },
            value: 1,
        },
    };

    const events = {
        click: (event) => {
            if (event.nodes.length === 1) {
                handleClickOpen(event.nodes[0]);
                sessionStorage.setItem('nodeId', event.nodes[0]);
            }
        },
    };

    // 處理任務操作
    const handleTaskAction = (taskId, action) => {
        console.log(`任務 ${taskId} 執行操作: ${action}`);
        alert(`執行操作: ${action} (任務ID: ${taskId})`);
    };
    
    // 處理節點創建完成
    const handleNodeCreated = async (createdNode) => {
        console.log('🎉 [IdeaWall] ========== 收到節點創建完成通知 ==========');
        console.log('  - 創建的節點完整數據:', JSON.stringify(createdNode, null, 2));
        console.log('  - 節點ID:', createdNode.id);
        console.log('  - 節點標題:', createdNode.title);
        console.log('  - 節點類型:', createdNode.node_type);
        console.log('  - 節點內容:', createdNode.content?.substring(0, 50));
        console.log('  - 作者ID:', createdNode.author_id);
        console.log('  - 作者名稱:', createdNode.author_name);
        console.log('  - 小組ID:', createdNode.group_id);
        console.log('  - 課程派發ID:', createdNode.course_dispatch_id);
        console.log('  - 創建時間:', createdNode.created_at || createdNode.createdAt);
        
        try {
            // 立即更新本地狀態，讓創建者能看到新節點
            console.log('🔄 [IdeaWall] 開始處理新節點到本地狀態');
            const convertedNodes = await processWallNodesResponse([createdNode], [], true);
            console.log('✅ [IdeaWall] 節點已添加到本地狀態');
            console.log('  - 轉換後的節點數量:', convertedNodes?.length || 0);
            console.log('  - 轉換後的節點:', convertedNodes);
            
            // 檢查圖表狀態
            console.log('📊 [IdeaWall] 當前圖表狀態:');
            console.log('  - 節點數量:', graph.nodes.length);
            console.log('  - 邊緣數量:', graph.edges.length);
            console.log('  - allNodes 數量:', allNodes.length);
            
            console.log('========== 節點創建完成處理結束 ==========');
        } catch (error) {
            console.error('❌ [IdeaWall] 處理新節點時發生錯誤:', error);
            console.error('  - 錯誤堆棧:', error.stack);
        }
    };
    
    // 打開提交對話框
    // 檢查是否為系統自動生成的狀態變更訊息
    const isSystemGeneratedMessage = (content) => {
        if (!content || typeof content !== 'string') return false;
        // 檢查是否包含系統自動生成的關鍵字
        return content.includes('組員在想法牆上發表節點') || 
               content.includes('任務自動開始') ||
               content.includes('原狀態：');
    };

    const handleOpenSubmissionDialog = (task) => {
        setSelectedTask(task);
        // 如果已有繳交紀錄，預填寫內容（但過濾掉系統自動生成的訊息）
        let submissionContent = '';
        if (task.taskSubmission && task.taskSubmission.submission_content) {
            // 只使用非系統生成的內容，且狀態為 completed 的提交
            if (task.taskSubmission.status === 'completed' && 
                !isSystemGeneratedMessage(task.taskSubmission.submission_content)) {
                submissionContent = task.taskSubmission.submission_content;
            }
        } else if (task.groupSubmission && task.groupSubmission.content) {
            // 只使用非系統生成的內容
            if (!isSystemGeneratedMessage(task.groupSubmission.content)) {
                submissionContent = task.groupSubmission.content;
            }
        }
        setSubmissionText(submissionContent);
        setOpenSubmissionDialog(true);
    };
    
    // 關閉提交對話框
    const handleCloseSubmissionDialog = () => {
        setOpenSubmissionDialog(false);
        setSelectedTask(null);
        setSubmissionText('');
    };
    
    // 提交任務（改進版：支援小組級別狀態同步）
    const handleSubmitTask = async () => {
        try {
            // 設置提交載入狀態
            setIsSubmittingTask(true);
            
            // 獲取當前用戶信息
            const currentUserId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
            const currentUserName = sessionStorage.getItem('userName') || localStorage.getItem('userName') || '當前學生';
            
            // 調用 API 提交任務，使用 taskId 而不是節點 ID
            const taskId = selectedTask.taskId || selectedTask.id;
            console.log('提交任務，taskId:', taskId, 'selectedTask:', selectedTask);
            
            // 簡化狀態處理：直接提交成果
        if (!submissionText.trim()) {
            alert('請輸入繳交內容');
            setIsSubmittingTask(false);
            return;
            }
            
            const newStatus = 'completed';
            const submissionContent = submissionText;
            
            console.log('📤 [Frontend] 發送任務提交請求:');
            console.log('  - taskId:', taskId);
            console.log('  - newStatus:', newStatus);
            console.log('  - submissionContent:', submissionContent);
            
            const response = await axiosInstance.put(`${config.updateStudentTaskStatus}${taskId}/status`, {
                status: newStatus,
                submission_content: submissionContent
            });
            
            if (response.data.success) {
                // 使用新的小組級別狀態更新邏輯
                await updateGroupTaskStatus(taskId, newStatus, response.data.data);
                
                alert('✅ 成果繳交成功！');
                handleCloseSubmissionDialog();
            } else {
                alert('❌ 繳交失敗：' + (response.data.message || '未知錯誤'));
            }
        } catch (error) {
            console.error('提交任務失敗:', error);
            alert('提交失敗：' + (error.response?.data?.message || error.message || '網路錯誤'));
        } finally {
            setIsSubmittingTask(false); // 無論成功或失敗，都關閉載入狀態
        }
    };

    // 更新小組任務狀態（新增）
    const updateGroupTaskStatus = async (taskId, newStatus, responseData) => {
        try {
            console.log('🔄 [前端] 開始更新小組任務狀態');
            console.log('  - taskId:', taskId);
            console.log('  - newStatus:', newStatus);
            console.log('  - responseData:', responseData);
            
            // 獲取小組任務狀態
            const activeGroupId = urlGroupId || sessionStorage.getItem('groupId') || localStorage.getItem('groupId');
            const groupStatusResponse = await axiosInstance.get(`${config.getGroupTaskStatus}${taskId}/group-status?groupId=${activeGroupId}`);
            
            console.log('📥 [前端] 獲取小組狀態響應:');
            console.log('  - success:', groupStatusResponse.data.success);
            console.log('  - data:', groupStatusResponse.data.data);
            
            if (groupStatusResponse.data.success) {
                const groupData = groupStatusResponse.data.data;
                const groupStatus = groupData.group_status;
                const memberSubmissions = groupData.member_submissions;
                
                console.log('📊 [前端] 解析後的數據:');
                console.log('  - groupStatus:', groupStatus);
                console.log('  - memberSubmissions:', memberSubmissions);
                
                // 更新任務公告狀態（使用函數式更新，確保基於最新狀態）
                setTaskAnnouncements(prevTasks => {
                    // 如果任務列表為空且尚未載入完成，可能需要重新獲取
                    // 如果已載入完成，可能是本來就沒有任務，仍然可以更新（可能是新任務）
                    if (prevTasks.length === 0 && !tasksLoaded) {
                        console.log('⚠️ [updateGroupTaskStatus] 任務列表為空且尚未載入，重新獲取任務列表');
                        fetchTasks();
                        return prevTasks; // 返回空列表，等待 fetchTasks 完成
                    }
                    
                    const updatedTasks = prevTasks.map(task => {
                        // 使用 taskId 或 task.taskId 來匹配
                        if (task.id === taskId || task.taskId === taskId) {
                            // 找到當前用戶的最新提交記錄
                            const currentUserId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
                            const currentUserSubmission = memberSubmissions.find(sub => 
                                sub.member && sub.member.id == currentUserId && sub.submission
                            );
                            
                            const updatedTask = {
                                ...task,
                                taskStatus: newStatus, // 直接使用新狀態，不依賴 groupStatus
                                groupStatus: groupStatus,
                                memberSubmissions: memberSubmissions,
                                lastUpdated: new Date().toISOString(),
                                // 添加繳交記錄信息 - 使用當前用戶的提交記錄
                                taskSubmission: currentUserSubmission && currentUserSubmission.submission ? {
                                    submission_content: currentUserSubmission.submission.submission_content,
                                    status: currentUserSubmission.submission.status,
                                    submitted_at: currentUserSubmission.submission.submitted_at,
                                    is_latest: currentUserSubmission.submission.is_latest
                                } : null
                            };
                            
                            console.log('✅ [前端] 更新任務:', task.title);
                            console.log('  - 舊狀態:', task.taskStatus);
                            console.log('  - 新狀態:', updatedTask.taskStatus);
                            console.log('  - taskSubmission:', updatedTask.taskSubmission);
                            console.log('  - currentUserId:', currentUserId);
                            console.log('  - currentUserSubmission:', currentUserSubmission);
                            
                            return updatedTask;
                        }
                        return task;
                    });
                    
                    console.log('🔄 [前端] 設置更新後的任務公告');
                    return updatedTasks;
                });
                
                // 如果 WebSocket 可用，發送狀態更新事件
                if (ws) {
                    ws.emit('task-status-updated', {
                        task_id: taskId,
                        group_id: groupData.group_id,
                        status: newStatus,
                        updated_by: responseData.updated_by,
                        updated_at: responseData.updated_at
                    });
                }
            }
        } catch (error) {
            console.error('❌ [前端] 更新小組任務狀態失敗:', error);
            console.error('  - 錯誤詳情:', error.response?.data);
            
            // 回退到本地狀態更新（使用函數式更新）
            setTaskAnnouncements(prevTasks => {
                // 如果任務列表為空且尚未載入完成，重新獲取
                if (prevTasks.length === 0 && !tasksLoaded) {
                    console.log('⚠️ [updateGroupTaskStatus] 錯誤處理：任務列表為空且尚未載入，重新獲取任務列表');
                    fetchTasks();
                    return prevTasks;
                }
                
                return prevTasks.map(task => {
                    if (task.id === taskId || task.taskId === taskId) {
                        return {
                            ...task,
                            taskStatus: newStatus,
                            lastUpdated: new Date().toISOString(),
                            // 添加基本的繳交記錄信息
                            taskSubmission: {
                                submission_content: submissionText,
                                status: newStatus,
                                submitted_at: new Date().toISOString()
                            }
                        };
                    }
                    return task;
                });
            });
        }
    };

    // 檢查回覆節點是否與任務相關，並自動更新任務狀態（基於邊緣關係）
    const checkAndUpdateTaskStatusFromReply = async (replyNode) => {
        try {
            console.log('🔍 [自動任務更新] ========== 開始檢查回覆節點 ==========');
            console.log('  - 回覆節點 ID:', replyNode.id);
            console.log('  - 回覆節點標題:', replyNode.title);
            console.log('  - 回覆節點類型:', replyNode.node_type);
            console.log('  - 節點任務ID:', replyNode.task_id);
            console.log('  - 父節點 ID:', replyNode.parent_node_id);
            
            // 顯示當前圖表狀態
            console.log('📊 [自動任務更新] 當前圖表狀態:');
            console.log('  - 節點數量:', allNodes.length);
            console.log('  - 邊緣數量:', graph.edges.length);
            console.log('  - 任務公告數量:', taskAnnouncements.length);
            
            // 顯示所有邊緣信息
            console.log('🔗 [自動任務更新] 所有邊緣信息:');
            graph.edges.forEach((edge, index) => {
                console.log(`  - 邊緣 ${index + 1}:`, {
                    from: edge.from,
                    to: edge.to,
                    edge_type: edge.edge_type,
                    id: edge.id
                });
            });
            
            let relatedTask = null;
            
            // 方法1：檢查回覆節點本身是否關聯任務
            console.log('🔍 [自動任務更新] 方法1: 檢查回覆節點本身是否關聯任務');
            if (replyNode.task_id) {
                relatedTask = taskAnnouncements.find(task => task.taskId === replyNode.task_id);
                console.log('  - 節點任務ID:', replyNode.task_id);
                console.log('  - 找到相關任務:', relatedTask ? relatedTask.title : '無');
            } else {
                console.log('  - 回覆節點沒有直接關聯任務');
            }
            
            // 方法2：通過邊緣關係查找父節點（不限制 edge_type）
            if (!relatedTask) {
                console.log('🔍 [自動任務更新] 方法2: 通過邊緣關係查找父節點');
                
                // 查找指向此節點的邊緣（不限制 edge_type，包括所有類型的連線）
                const incomingEdges = graph.edges.filter(edge => 
                    edge.to === replyNode.id
                );
                
                console.log('  - 查找條件: to=' + replyNode.id + ' (所有邊緣類型)');
                console.log('  - 找到的入邊緣數量:', incomingEdges.length);
                
                if (incomingEdges.length > 0) {
                    const parentNodeId = incomingEdges[0].from;
                    console.log('  - 父節點 ID:', parentNodeId);
                    
                    const parentNode = allNodes.find(node => node.id === parentNodeId);
                    console.log('  - 找到父節點:', parentNode ? '是' : '否');
                    
                    if (parentNode) {
                        console.log('  - 父節點信息:', {
                            id: parentNode.id,
                            title: parentNode.title,
                            task_id: parentNode.task_id,
                            node_type: parentNode.node_type
                        });
                        
                        if (parentNode.task_id) {
                            relatedTask = taskAnnouncements.find(task => task.taskId === parentNode.task_id);
                            console.log('  - 通過父節點找到相關任務:', relatedTask ? relatedTask.title : '無');
                        } else {
                            console.log('  - 父節點沒有關聯任務');
                        }
                    }
                } else {
                    console.log('  - 沒有找到指向此節點的邊緣');
                }
            }
            
            // 方法3：如果都沒有，檢查是否有任何待開始的任務（pending 或 dispatched）
            // 注意：這個方法會自動關聯第一個待開始的任務，可能不是最合適的
            // 但為了確保任務狀態能夠自動更新，我們還是要嘗試
            if (!relatedTask) {
                console.log('🔍 [自動任務更新] 方法3: 檢查是否有待開始的任務');
                
                // 查找狀態為 pending 或 dispatched 的任務
                const pendingTasks = taskAnnouncements.filter(task => 
                    task.taskStatus === 'pending' || task.taskStatus === 'dispatched'
                );
                console.log('  - 待開始任務數量 (pending + dispatched):', pendingTasks.length);
                console.log('  - 待開始任務列表:', pendingTasks.map(t => ({ id: t.taskId, title: t.title, status: t.taskStatus })));
                
                if (pendingTasks.length > 0) {
                    // 優先選擇 dispatched 狀態的任務，否則選擇 pending
                    const dispatchedTask = pendingTasks.find(task => task.taskStatus === 'dispatched');
                    relatedTask = dispatchedTask || pendingTasks[0];
                    console.log('  - 自動關聯任務:', relatedTask.title, '狀態:', relatedTask.taskStatus);
                    console.log('  - ⚠️ 注意：這是自動關聯，可能不是最精確的任務');
                } else {
                    console.log('  - 沒有待開始的任務');
                }
            }
            
            if (!relatedTask) {
                console.log('⚠️ [自動任務更新] 未找到相關任務，跳過任務狀態更新');
                console.log('========== 檢查結束 ==========');
                return;
            }
            
            console.log('✅ [自動任務更新] 找到相關任務，準備更新狀態');
            console.log('  - 任務ID:', relatedTask.taskId);
            console.log('  - 任務標題:', relatedTask.title);
            console.log('  - 當前狀態:', relatedTask.taskStatus);
            console.log('  - 是否需要繳交:', relatedTask.requiresSubmission);
            
            // 如果任務狀態是 pending 或 dispatched，則自動更新為 in_progress
            if (relatedTask.taskStatus === 'pending' || relatedTask.taskStatus === 'dispatched') {
                const statusText = relatedTask.taskStatus === 'pending' ? 'pending' : 'dispatched';
                console.log(`🚀 [自動任務更新] 任務狀態為 ${statusText}，自動更新為 in_progress`);
                
                try {
                    const updateData = {
                        status: 'in_progress',
                        submission_content: `組員在想法牆上發表節點，任務自動開始（原狀態：${statusText}）`
                    };
                    
                    console.log('📤 [自動任務更新] 發送 API 請求:');
                    console.log('  - URL:', `${config.updateStudentTaskStatus}${relatedTask.taskId}/status`);
                    console.log('  - 數據:', updateData);
                    
                    const response = await axiosInstance.put(`${config.updateStudentTaskStatus}${relatedTask.taskId}/status`, updateData);
                    
                    console.log('📥 [自動任務更新] API 響應:');
                    console.log('  - 成功:', response.data.success);
                    console.log('  - 消息:', response.data.message);
                    console.log('  - 數據:', response.data.data);
                    
                    if (response.data.success) {
                        console.log('✅ [自動任務更新] 任務狀態更新成功');
                        await updateGroupTaskStatus(relatedTask.taskId, 'in_progress', response.data.data);
                        
                        // 顯示通知
                        console.log(`📢 [自動任務更新] 任務已自動開始：${relatedTask.title} (${statusText} → in_progress)`);
                    } else {
                        console.error('❌ [自動任務更新] 任務狀態更新失敗:', response.data.message);
                    }
                } catch (error) {
                    console.error('❌ [自動任務更新] 更新任務狀態時發生錯誤:');
                    console.error('  - 錯誤類型:', error.name);
                    console.error('  - 錯誤消息:', error.message);
                    console.error('  - 響應狀態:', error.response?.status);
                    console.error('  - 響應數據:', error.response?.data);
                    console.error('  - 完整錯誤:', error);
                }
            } else {
                console.log('ℹ️ [自動任務更新] 任務狀態不是 pending 或 dispatched，無需更新');
                console.log('  - 當前狀態:', relatedTask.taskStatus);
            }
            
            console.log('========== 檢查結束 ==========');
            
        } catch (error) {
            console.error('❌ [自動任務更新] 檢查回覆節點時發生錯誤:', error);
            console.error('  - 錯誤堆棧:', error.stack);
        }
    };

    // 任務公告組件
    const TaskAnnouncementArea = () => (
        <div style={{
            position: 'fixed',
            top: '110px',
            right: '20px',
            width: isTaskDrawerExpanded ? '340px' : '60px',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: isTaskDrawerExpanded ? '20px' : '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid #e8f4f8',
            zIndex: 1000,
            maxHeight: 'calc(100vh - 140px)',
            overflowY: isTaskDrawerExpanded ? 'auto' : 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: isTaskDrawerExpanded ? '20px' : '0',
                padding: isTaskDrawerExpanded ? '0' : '8px',
                borderRadius: isTaskDrawerExpanded ? '0' : '8px',
                transition: 'background-color 0.2s ease'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isTaskDrawerExpanded ? '18px' : '20px',
                    fontWeight: 'bold',
                    color: '#1e4d5f',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    width: '100%',
                    textAlign: 'center'
            }}
            onClick={() => setIsTaskDrawerExpanded(!isTaskDrawerExpanded)}
            onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
                >
                    <span style={{ 
                        fontSize: isTaskDrawerExpanded ? '20px' : '24px'
                    }}>📋</span>
                    {isTaskDrawerExpanded && <span style={{ marginLeft: '10px' }}>任務公告區</span>}
                </div>
                
                {isTaskDrawerExpanded && (
                    <div style={{
                        fontSize: '12px',
                        color: '#1e4d5f',
                        backgroundColor: '#e8f4f8',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        border: '1px solid #b8dce8',
                        fontWeight: '650',
                        whiteSpace: 'nowrap' // 確保文字在一行顯示
                    }}>
                        {taskAnnouncements.length} 項任務
                    </div>
                )}
            </div>
            
            {isTaskDrawerExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* 載入中提示 */}
                    {isLoadingTasks && (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#666',
                            fontSize: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <div className="wall-loading-spinner" style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #e3f2fd',
                                borderTop: '2px solid #2196F3',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <div>載入任務中...</div>
                        </div>
                    )}
                    
                    {/* 已載入完成但為空的情況 */}
                    {!isLoadingTasks && tasksLoaded && taskAnnouncements.length === 0 && (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#999',
                            fontSize: '13px',
                            fontStyle: 'italic'
                        }}>
                            目前沒有任務公告
                        </div>
                    )}
                    
                    {/* 任務列表 */}
                    {!isLoadingTasks && taskAnnouncements.map((task) => (
                    <div key={task.id} style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                    }}
                    onClick={() => handleTaskAnnouncementClick(task)}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                            <span style={{
                                fontSize: '20px',
                                marginRight: '8px'
                            }}>
                                {task.icon}
                            </span>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#333',
                                lineHeight: '1.3'
                            }}>
                                {task.title}
                                {task.hasBrainstormWall && (
                                    <span style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        backgroundColor: '#ff9800',
                                        color: 'white',
                                        padding: '2px 6px',
                                        borderRadius: '10px',
                                        fontWeight: 'normal'
                                    }}>
                                        💡 想法收集
                                    </span>
                                )}
                            </div>
                          </div>
                          
                          {/* 問題牆提示與跳轉按鈕 */}
                          {task.hasQuestionWall && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // 記錄當前任務 ID，供問題牆頁面自動聚焦對應關鍵提問
                                if (task.taskId) {
                                  sessionStorage.setItem('questionWallTargetTaskId', String(task.taskId));
                                }
                                navigate(`/student/question-wall/${courseDispatchId}`);
                              }}
                              style={{
                                flexShrink: 0,
                                padding: '4px 8px',
                                fontSize: '12px',
                                borderRadius: '12px',
                                border: '1px solid #1976d2',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title="前往問題牆回應此任務的提問"
                            >
                              ❓ 到問題牆作答
                            </button>
                          )}
                        </div>
                        
                        <div style={{
                            fontSize: '13px',
                            color: '#666',
                            lineHeight: '1.4',
                            marginBottom: '8px'
                        }}>
                            {task.content}
                        </div>
                        
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '12px',
                            color: '#999',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              {task.author} · {task.time}
                            </span>
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 'bold',
                                color: '#666',
                                backgroundColor: '#f5f5f5',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}
                            >
                              {task.typeLabel}
                            </span>
                        </div>
                        
                        {task.requiresSubmission && (
                            <>
                                {/* 簡化的狀態顯示 - 只顯示是否需要繳交和當前繳交狀態 */}
                                <div style={{
                                    fontSize: '11px',
                                    padding: '6px 8px',
                                    backgroundColor: task.taskStatus === 'completed' ? '#e8f5e9' : '#f5f5f5',
                                    borderRadius: '4px',
                                    border: task.taskStatus === 'completed' ? '1px solid #c8e6c9' : '1px solid #e0e0e0',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{ 
                                        fontWeight: 'bold', 
                                        color: task.taskStatus === 'completed' ? '#2e7d32' : '#757575',
                                        marginBottom: '4px'
                                    }}>
                                        {task.taskStatus === 'completed' ? '✅ 已完成繳交' : '📝 需要繳交成果'}
                                    </div>
                                    
                                    {task.taskStatus === 'completed' && task.taskSubmission && 
                                     !isSystemGeneratedMessage(task.taskSubmission.submission_content) && (
                                        <div style={{ color: '#666', fontSize: '10px' }}>
                                            已繳交：{task.taskSubmission.submission_content?.substring(0, 30)}...
                                            {task.taskSubmission.submitted_by && task.taskSubmission.submitted_by !== '組員' && (
                                                <div style={{ color: '#999', fontSize: '9px', marginTop: '2px' }}>
                                                    提交者：{task.taskSubmission.submitted_by}
                                            </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {task.taskStatus === 'completed' && (
                                        <div style={{ color: '#666', fontSize: '10px' }}>
                                            老師已確認完成，仍可修改內容
                                        </div>
                                    )}
                                    
                                    {!task.taskStatus || task.taskStatus === 'pending' || task.taskStatus === 'in_progress' ? (
                                        <div style={{ color: '#666', fontSize: '10px' }}>
                                            請在想法牆上討論後繳交成果
                                </div>
                                    ) : null}
                                </div>
                                
                                {/* 繳交按鈕 - 始終顯示，讓學生知道可以繳交 */}
                                {viewingGroupId && myGroupId && String(viewingGroupId) !== String(myGroupId) ? (
                                    <div style={{
                                        width: '100%',
                                        padding: '8px',
                                        backgroundColor: '#f5f5f5',
                                        color: '#999',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        cursor: 'not-allowed'
                                    }}>
                                        ⚠️ 查看其他小組，無法繳交
                                    </div>
                                ) : (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenSubmissionDialog(task);
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            backgroundColor: task.taskStatus === 'completed' ? '#4caf50' : '#2e7d32',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '0.8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '1';
                                        }}
                                    >
                                        {task.taskStatus === 'completed' ? '修改繳交內容' : '繳交成果'}
                                    </button>
                                )}
                            </>
                        )}
                        
                        {!task.requiresSubmission && (
                            <div style={{
                                fontSize: '11px',
                                padding: '4px 8px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                color: '#666',
                                textAlign: 'center'
                            }}>
                                ℹ️ 不需繳交
                            </div>
                        )}
                    </div>
                    ))}
                </div>
            )}
        </div>
    );

    // 加載狀態
    if (loading) {
        return (
            <div className="student-wall-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>載入中...</div>
            </div>
        );
    }

    // 錯誤狀態
    if (error) {
        return (
            <div className="student-wall-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div style={{ color: 'red' }}>錯誤: {error}</div>
            </div>
        );
    }

    return (
        <div className="student-wall-container">
            {/* 統一導航列 */}
            <StudentWallNavbar
                currentWall={currentWall}
                onWallChange={handleWallChange}
                courseDispatchId={courseDispatchId}
                groupId={urlGroupId || sessionStorage.getItem('groupId')}
            />
            
            {/* 老師模式：新增便利貼按鈕 */}
            {isTeacher && (
                <div style={{ 
                    position: 'fixed', 
                    top: '80px', 
                    right: '20px', 
                    zIndex: 1000 
                }}>
                    <Button
                        variant="contained"
                        startIcon={<span>➕</span>}
                        onClick={() => {
                            // 這裡可以添加新增便利貼的邏輯
                            console.log('老師新增便利貼');
                        }}
                        sx={{ 
                            backgroundColor: '#1e4d5f',
                            '&:hover': { backgroundColor: '#0d3b4a' }
                        }}
                    >
                        新增便利貼
                    </Button>
                </div>
            )}
            
            {/* 左側抽屜 */}
            <WallDrawer
                currentWall={currentWall}
                onWallChange={handleWallChange}
                onNodeTypeSelect={handleNodeTypeSelect}
                wallConfig={wallConfig}
                nodeTypes={nodeTypes}
                visualizationMode={visualizationMode}
                onVisualizationModeChange={setVisualizationMode}
                timeFilter={timeFilter}
                groupFilter={groupFilter}
                keywordFilter={keywordFilter}
                onTimeFilterChange={handleTimeFilterChange}
                onGroupFilterChange={handleGroupFilterChange}
                onKeywordFilterChange={handleKeywordFilterChange}
                onFilterButtonClick={() => setFilterPopupVisible(true)}
                ws={ws}
                onNodeCreated={handleNodeCreated}
                disableCreateNode={viewingGroupId && myGroupId && String(viewingGroupId) !== String(myGroupId)}
                allGroups={allGroups}
                myGroupId={myGroupId}
                viewingGroupId={viewingGroupId}
                onGroupChange={handleGroupChange}
                isGroupSelectDialogOpen={isGroupSelectDialogOpen}
                onGroupSelectDialogOpen={setIsGroupSelectDialogOpen}
                allowCrossGroupView={allowCrossGroupView}
            />
            
            {/* 主內容區域 */}
            <div className="student-wall-main">
                <div className="wall-content idea-wall-content" style={{ marginLeft: 0, width: '100%', height: '100%' }}>
            
            {/* 篩選彈窗 */}
            {isFilterPopupVisible && (
                        <>
                            {/* 背景遮罩，點擊時關閉 */}
                            <div 
                                style={{
                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    zIndex: 99999,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setFilterPopupVisible(false)}
                            />
                            <div style={{
                                position: 'fixed',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '400px',
                                backgroundColor: '#ffffff',
                                borderRadius: '16px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                border: '1px solid #e8f4f8',
                                padding: '30px',
                    zIndex: 100000,
                                textAlign: 'center'
                            }}>
                                <h3 style={{ fontWeight: 'bold', margin: 0, padding: '0 0 20px 0', color: '#1e4d5f' }}>篩選條件</h3>
                                <div style={{ margin: '20px 0' }}>
                                    <label htmlFor="timeFilter" style={{ display: 'block', marginBottom: '10px', textAlign: 'left', fontWeight: '600' }}>時間範圍:</label>
                            <input
                                type="number"
                                id="timeFilter"
                                value={timeFilter}
                                onChange={handleTimeFilterChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '14px'
                                        }}
                                min="0"
                                        placeholder="輸入分鐘數"
                            />
                                    <span style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>分鐘內</span>
                        </div>
                                <div style={{ margin: '20px 0' }}>
                                    <label htmlFor="groupFilter" style={{ display: 'block', marginBottom: '10px', textAlign: 'left', fontWeight: '600' }}>篩選類型:</label>
                            <select
                                id="groupFilter"
                                value={groupFilter}
                                onChange={handleGroupFilterChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '14px'
                                        }}
                            >
                                <option value="all">全部</option>
                                <option value="idea">想法💡</option>
                                <option value="question">問題❓</option>
                                <option value="record">記錄📄</option>
                                <option value="experiment">實驗🧪</option>
                                        <option value="info">資訊🔍</option>
                                <option value="reply">回覆💬</option>
                            </select>
                        </div>
                                <div style={{ margin: '20px 0' }}>
                                    <label htmlFor="keywordFilter" style={{ display: 'block', marginBottom: '10px', textAlign: 'left', fontWeight: '600' }}>關鍵字:</label>
                            <input
                                type="text"
                                id="keywordFilter"
                                value={keywordFilter}
                                onChange={handleKeywordFilterChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '14px'
                                        }}
                                placeholder="輸入標題或內容關鍵字"
                            />
                        </div>
                                <div style={{ marginTop: '30px', display: 'flex', gap: '10px', flexDirection: 'column' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleClearAllFilters}
                                        style={{
                                            borderColor: '#ff9800',
                                            color: '#ff9800',
                                            fontWeight: 'bold',
                                            width: '100%',
                                            height: '40px',
                                            borderRadius: '8px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        取消全部篩選
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => setFilterPopupVisible(false)}
                                        style={{
                                            backgroundColor: '#2b687f',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            width: '100%',
                                            height: '40px',
                                            borderRadius: '8px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        關閉篩選
                                    </Button>
                    </div>
                </div>
                        </>
                    )}
                    
                    {/* 想法牆大白板 */}
                    <div id="graph" className="idea-wall-canvas" style={{ width: '100%', height: '100%', position: 'relative' }}>
                        {/* 節點載入中提示 */}
                        {isLoadingNodes && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1000,
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '30px 40px',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '15px',
                                minWidth: '200px'
                            }}>
                                <div className="wall-loading-spinner" style={{
                                    width: '40px',
                                    height: '40px',
                                    border: '4px solid #e3f2fd',
                                    borderTop: '4px solid #2196F3',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                                <div style={{
                                    color: '#333',
                                    fontSize: '16px',
                                    fontWeight: '500'
                                }}>載入節點中...</div>
                                {isSwitchingGroup && (
                                    <div style={{
                                        color: '#666',
                                        fontSize: '13px'
                                    }}>切換小組中，請稍候</div>
                                )}
                            </div>
                        )}
                        
                        {/* 已載入完成但為空的情況 */}
                        {!isLoadingNodes && nodesLoaded && graph.nodes.length === 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 100,
                                textAlign: 'center',
                                color: '#999',
                                fontSize: '16px',
                                fontStyle: 'italic',
                                padding: '20px'
                            }}>
                                目前沒有節點
                            </div>
                        )}
                        
                        {visualizationMode === 'vis-network' ? (
                        <Graph
                            key={graphKey} 
                            graph={graph}
                            options={options}
                            events={events}
                            getNetwork={(network) => {
                                networkRef.current = network;
                            }}
                        />
                        ) : (
                            <D3IdeaWall
                                nodes={graph.nodes}
                                edges={graph.edges}
                                onNodeClick={handleClickOpen}
                                width={1200}
                                height={800}
                                focusNodeId={focusNodeId}
                            />
                        )}
                    </div>
                </div>
            </div>
            
            {/* 任務公告區域 - 只在查看自己小組時顯示 */}
            {myGroupId && viewingGroupId && String(viewingGroupId) === String(myGroupId) && (
                <TaskAnnouncementArea />
            )}
            

            <ViewNode 
                open={open} 
                onClose={handleClose} 
                nodeContent={nodeContent} 
                ws={ws} 
                groupId={sessionStorage.getItem('viewingGroupId') || viewingGroupId || urlGroupId || groupId || sessionStorage.getItem('groupId')}
                wallType="idea"
            />
            
            {/* 任務提交對話框 */}
            <Dialog 
                open={openSubmissionDialog} 
                onClose={isSubmittingTask ? undefined : handleCloseSubmissionDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedTask?.taskStatus === 'completed' ? '修改任務繳交內容' : '提交任務'}
                    {isSubmittingTask && (
                        <span style={{
                            marginLeft: '10px',
                            fontSize: '14px',
                            color: '#666',
                            fontWeight: 'normal',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <div className="wall-loading-spinner" style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #e3f2fd',
                                borderTop: '2px solid #2196F3',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            提交中...
                        </span>
                    )}
                </DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: '16px', marginTop: '8px' }}>
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: '8px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                {selectedTask?.title}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                {selectedTask?.content}
                            </div>
                        </div>
                        
                        {selectedTask?.groupSubmission && (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '8px',
                                marginBottom: '16px',
                                fontSize: '13px'
                            }}>
                                <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: '4px' }}>
                                    📌 之前的提交記錄
                                </div>
                                <div style={{ color: '#666' }}>
                                    提交者：{selectedTask.groupSubmission.submittedBy}
                                </div>
                                <div style={{ color: '#999', fontSize: '12px' }}>
                                    提交時間：{selectedTask.groupSubmission.submittedAt}
                                </div>
                            </div>
                        )}
                        
                        <TextField
                            autoFocus
                            margin="dense"
                            label="繳交內容"
                            placeholder="請輸入作業內容、心得或說明..."
                            multiline
                            rows={6}
                            fullWidth
                            variant="outlined"
                            value={submissionText}
                            onChange={(e) => setSubmissionText(e.target.value)}
                        />
                    </div>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                    <Button 
                        onClick={handleCloseSubmissionDialog}
                        disabled={isSubmittingTask}
                    >
                        取消
                    </Button>
                    <Button 
                        onClick={handleSubmitTask} 
                        variant="contained"
                        disabled={!submissionText.trim() || isSubmittingTask}
                        startIcon={isSubmittingTask ? (
                            <div className="wall-loading-spinner" style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderTop: '2px solid #ffffff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                        ) : null}
                    >
                        {isSubmittingTask ? '提交中...' : (selectedTask?.taskStatus === 'completed' ? '修改繳交內容' : '繳交成果')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

